import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { 
  Building2, MapPin, Calendar, Package, Globe, Handshake, Award,
  User, Mail, Phone, Link as LinkIcon, Video, Youtube, Upload,
  ArrowRight, ArrowLeft, Check, Sparkles, Loader2, Eye, Save, Image, X,
  RefreshCw, Target, MessageSquare, Briefcase, Play, EyeOff, Send, Clock,
  Gift, AlertTriangle, TrendingUp, MousePointer, BarChart3, Share2,
  ToggleLeft, ToggleRight, FileText, FolderOpen, CheckCircle, XCircle,
  Languages, Edit3, Download, QrCode, CalendarDays, Trash2, Copy, ExternalLink
} from 'lucide-react';
import ProfileScoreWidget from '../components/ProfileScoreWidget';
import ShowcaseManager from '../components/ShowcaseManager';
import OpportunityManager from '../components/OpportunityManager';
import { FeatureLock } from '../components/FeatureLock';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Step indicator component
function StepIndicator({ currentStep, steps }) {
  return (
    <div className="flex items-center justify-center mb-8">
      {steps.map((step, index) => (
        <div key={index} className="flex items-center">
          <div className={`
            w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm
            ${index < currentStep ? 'bg-[#C41E3A] text-white' : 
              index === currentStep ? 'bg-[#C41E3A] text-white ring-4 ring-[#C41E3A]/30' : 
              'bg-zinc-800 text-zinc-500'}
          `}>
            {index < currentStep ? <Check className="w-5 h-5" /> : index + 1}
          </div>
          {index < steps.length - 1 && (
            <div className={`w-16 h-1 mx-2 ${index < currentStep ? 'bg-[#C41E3A]' : 'bg-zinc-800'}`} />
          )}
        </div>
      ))}
    </div>
  );
}

// Supported languages for translation editing
const SUPPORTED_LANGUAGES = [
  { code: 'vi', name: 'Vietnamese', flag: '🇻🇳' },
  { code: 'th', name: 'Thai', flag: '🇹🇭' },
  { code: 'zh-CN', name: 'Chinese', flag: '🇨🇳' },
  { code: 'ja', name: 'Japanese', flag: '🇯🇵' },
  { code: 'hi', name: 'Hindi', flag: '🇮🇳' },
  { code: 'ar', name: 'Arabic', flag: '🇦🇪' },
  { code: 'id', name: 'Indonesian', flag: '🇮🇩' },
  { code: 'ms', name: 'Malay', flag: '🇲🇾' },
  { code: 'ko', name: 'Korean', flag: '🇰🇷' },
  { code: 'de', name: 'German', flag: '🇩🇪' },
  { code: 'fr', name: 'French', flag: '🇫🇷' },
  { code: 'es', name: 'Spanish', flag: '🇪🇸' }
];

// Editable translation fields
const TRANSLATION_FIELDS = [
  { path: 'tagline', label: 'Tagline' },
  { path: 'profile_sections.who_we_are', label: 'Who We Are' },
  { path: 'profile_sections.what_we_do', label: 'What We Do' },
  { path: 'profile_sections.why_choose_us', label: 'Why Choose Us' },
  { path: 'key_contact.bio_text', label: 'Key Contact Bio' },
  { path: 'key_contact.notable_achievements', label: 'Notable Achievements' }
];

// Translation Editor Component
function TranslationEditor({ profileId }) {
  const [selectedLang, setSelectedLang] = useState('vi');
  const [translations, setTranslations] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState('');

  // Fetch translations for selected language
  const fetchTranslations = async (lang) => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(
        `${API_URL}/api/profiles/${profileId}/translations/${lang}`,
        { withCredentials: true }
      );
      setTranslations(response.data.translations || {});
    } catch (err) {
      if (err.response?.status === 404) {
        setTranslations({});
        setError('No translations found for this language. View the public profile in this language first to generate translations.');
      } else {
        setError(err.response?.data?.detail || 'Failed to load translations');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (profileId && selectedLang) {
      fetchTranslations(selectedLang);
    }
  }, [profileId, selectedLang]);

  // Get nested value from translations object
  const getNestedValue = (obj, path) => {
    return path.split('.').reduce((current, key) => current?.[key], obj) || '';
  };

  // Start editing a field
  const startEdit = (field) => {
    setEditingField(field.path);
    setEditValue(getNestedValue(translations, field.path));
  };

  // Save edited translation
  const saveTranslation = async (fieldPath) => {
    setSaving(fieldPath);
    setError('');
    try {
      await axios.put(
        `${API_URL}/api/profiles/${profileId}/translations/${selectedLang}/edit`,
        {
          field_path: fieldPath,
          translated_text: editValue
        },
        { withCredentials: true }
      );
      
      // Update local state
      const updatedTranslations = { ...translations };
      const pathParts = fieldPath.split('.');
      if (pathParts.length === 1) {
        updatedTranslations[pathParts[0]] = editValue;
      } else if (pathParts.length === 2) {
        if (!updatedTranslations[pathParts[0]]) {
          updatedTranslations[pathParts[0]] = {};
        }
        updatedTranslations[pathParts[0]][pathParts[1]] = editValue;
      }
      setTranslations(updatedTranslations);
      
      setEditingField(null);
      setSuccess('Translation saved!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save translation');
    } finally {
      setSaving(null);
    }
  };

  const cancelEdit = () => {
    setEditingField(null);
    setEditValue('');
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-white font-semibold flex items-center gap-2">
          <Languages className="w-4 h-4 text-[#C41E3A]" />
          Translation Editor
          <span className="text-zinc-500 text-xs font-normal">(Admin Only)</span>
        </h3>
      </div>
      
      <p className="text-zinc-400 text-sm mb-4">
        Edit machine-translated content to fix errors or improve accuracy.
      </p>

      {/* Language Selector */}
      <div className="mb-4">
        <label className="block text-sm text-zinc-400 mb-2">Select Language</label>
        <div className="flex flex-wrap gap-2">
          {SUPPORTED_LANGUAGES.map((lang) => (
            <button
              key={lang.code}
              onClick={() => setSelectedLang(lang.code)}
              className={`px-3 py-1.5 rounded-lg text-sm flex items-center gap-1.5 transition-colors ${
                selectedLang === lang.code
                  ? 'bg-[#C41E3A] text-white'
                  : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
              }`}
            >
              <span>{lang.flag}</span>
              <span>{lang.name}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="mb-4 bg-red-900/20 border border-red-800 text-red-400 px-4 py-3 rounded-lg text-sm flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" />
          {error}
        </div>
      )}
      {success && (
        <div className="mb-4 bg-green-900/20 border border-green-800 text-green-400 px-4 py-3 rounded-lg text-sm flex items-center gap-2">
          <CheckCircle className="w-4 h-4" />
          {success}
        </div>
      )}

      {/* Translation Fields */}
      {loading ? (
        <div className="text-center py-8 text-zinc-500">
          <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
          Loading translations...
        </div>
      ) : Object.keys(translations).length > 0 ? (
        <div className="space-y-4">
          {TRANSLATION_FIELDS.map((field) => {
            const value = getNestedValue(translations, field.path);
            const isEditing = editingField === field.path;
            
            if (!value && !isEditing) return null;
            
            return (
              <div key={field.path} className="border border-zinc-700 rounded-lg p-4 bg-zinc-800/30">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-zinc-300">{field.label}</label>
                  {!isEditing && (
                    <button
                      onClick={() => startEdit(field)}
                      className="text-[#C41E3A] hover:text-white text-sm flex items-center gap-1"
                    >
                      <Edit3 className="w-3 h-3" />
                      Edit
                    </button>
                  )}
                </div>
                
                {isEditing ? (
                  <div className="space-y-2">
                    <textarea
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      rows={4}
                      className="w-full bg-zinc-900 border border-zinc-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C41E3A]"
                    />
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => saveTranslation(field.path)}
                        disabled={saving === field.path}
                        className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-lg text-sm flex items-center gap-1.5 disabled:opacity-50"
                      >
                        {saving === field.path ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <Check className="w-3 h-3" />
                        )}
                        Save
                      </button>
                      <button
                        onClick={cancelEdit}
                        className="bg-zinc-700 hover:bg-zinc-600 text-white px-3 py-1.5 rounded-lg text-sm flex items-center gap-1.5"
                      >
                        <X className="w-3 h-3" />
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="text-zinc-400 text-sm whitespace-pre-wrap">{value}</p>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="border-2 border-dashed border-zinc-700 rounded-lg p-6 text-center">
          <Languages className="w-8 h-8 text-zinc-600 mx-auto mb-2" />
          <p className="text-zinc-500 text-sm">No translations available</p>
          <p className="text-zinc-600 text-xs mt-1">
            View the profile in {SUPPORTED_LANGUAGES.find(l => l.code === selectedLang)?.name} to generate translations
          </p>
        </div>
      )}
      
      <div className="mt-4 p-3 bg-zinc-800/50 rounded-lg">
        <p className="text-xs text-zinc-500">
          <strong className="text-zinc-400">Tip:</strong> Use the{' '}
          <a href="/admin/translations" className="text-[#C41E3A] hover:underline">Translation Manager</a>{' '}
          to verify translations with DeepL before editing.
        </p>
      </div>
    </div>
  );
}

export default function ProfileEdit() {
  const { profileId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isRegeneratingAI, setIsRegeneratingAI] = useState(false);
  const [isGeneratingContactBio, setIsGeneratingContactBio] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [isUploadingContactPhoto, setIsUploadingContactPhoto] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const logoInputRef = useRef(null);
  const contactPhotoInputRef = useRef(null);
  
  // Testimonials state
  const [testimonials, setTestimonials] = useState([]);
  const [showTestimonialModal, setShowTestimonialModal] = useState(false);
  const [testimonialRequest, setTestimonialRequest] = useState({ contact_name: '', contact_email: '', message: '' });
  const [isRequestingTestimonial, setIsRequestingTestimonial] = useState(false);
  const [togglingVisibility, setTogglingVisibility] = useState(null);
  
  // Subscription & Coupon state
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [activeUntil, setActiveUntil] = useState(null);
  const [couponCode, setCouponCode] = useState('');
  const [couponValidation, setCouponValidation] = useState(null);
  const [isValidatingCoupon, setIsValidatingCoupon] = useState(false);
  const [isApplyingCoupon, setIsApplyingCoupon] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('6_months');
  const [showPromoInput, setShowPromoInput] = useState(false);
  const [showCallbackForm, setShowCallbackForm] = useState(false);
  const [callbackPhone, setCallbackPhone] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  
  // Analytics state
  const [analytics, setAnalytics] = useState(null);
  
  // Email Signature state
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [signatureData, setSignatureData] = useState({
    name: '',
    designation: '',
    email: '',
    phone: ''
  });
  const [isGeneratingSignature, setIsGeneratingSignature] = useState(false);
  
  // Event Invitations state
  const [invitations, setInvitations] = useState([]);
  const [showInvitationModal, setShowInvitationModal] = useState(false);
  const [invitationForm, setInvitationForm] = useState({
    event_title: '',
    event_type: 'seminar',
    event_date: '',
    event_time: '',
    venue: '',
    description: '',
    objectives: '',
    banner_image_url: ''
  });
  const [isCreatingInvitation, setIsCreatingInvitation] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState('blank');
  const [features, setFeatures] = useState(null);
  const canUseInvitations = !!features?.event_invitations;
  
  // Profile data state (for showcase)
  const [profileData, setProfileData] = useState(null);
  
  // Check if user is admin
  const isAdmin = user?.email?.toLowerCase() === 'brandstagevideo@gmail.com';
  const hasPaidTier = isAdmin || subscriptionStatus === 'active';
  
  // Profile category options (same as ProfileCreate)
  const profileCategories = [
    { id: 'general', label: 'General Profile', description: 'Standard company profile', free: true },
    { id: 'business_expansion', label: 'Business Expansion', description: 'Seeking growth opportunities', free: false },
    { id: 'international', label: 'International Market', description: 'Global market entry', free: false },
    { id: 'investment', label: 'Investment Invitation', description: 'Seeking investors', free: false },
    { id: 'trade_show', label: 'Trade Show / Event', description: 'Event presence', free: false },
    { id: 'partnership', label: 'Partnership Seeking', description: 'Strategic alliances', free: false },
    { id: 'announcement', label: 'Announcement', description: 'News & updates', free: false },
    { id: 'other', label: 'Other', description: 'Custom category', free: false }
  ];
  
  // Form data
  const [formData, setFormData] = useState({
    company_name: '',
    tagline: '',
    year_established: '',
    headquarters: '',
    products_services: '',
    key_markets: '',
    looking_for: '',
    certifications: [],
    
    // KPI Stats
    products_count: '',
    clients_count: '',
    countries_count: '',
    
    // Premium fields
    profile_category: 'general',
    custom_category: '',
    target_audience: '',
    key_message: '',
    profile_sections: null,  // { who_we_are, our_presence, what_we_seek, why_partner, call_to_action }
    audience_messages: [],
    
    logo_url: '',
    hero_video: null,
    additional_videos: [],
    contact_name: '',
    contact_designation: '',
    contact_email: '',
    contact_phone: '',
    website: '',
    include_key_contact: true,
    key_contact: {
      photo_url: '',
      years_experience: '',
      expertise_areas: [],
      achievements: '',
      linkedin_url: '',
      bio_text: '',
      videos: []
    },
    created_on_behalf_of: '',
    profile_text: '',
    
    // Resources/Documents link
    resources_enabled: false,
    resources_link: '',
    resources_label: 'Company Resources',
    
    // Multi-language translation
    multi_language_enabled: false,
    
    // Social URLs for score calculation
    social_urls: {
      linkedin: '',
      youtube: '',
      twitter: '',
      instagram: ''
    }
  });
  
  // Toggle for custom text vs AI sections
  const [useCustomText, setUseCustomText] = useState(false);
  
  // Resources link validation state
  const [resourcesLinkValid, setResourcesLinkValid] = useState(null); // null = not checked, true = valid, false = invalid
  const [resourcesLinkChecking, setResourcesLinkChecking] = useState(false);
  
  const steps = hasPaidTier ? ['Basics', 'Objective', 'Details', 'Media', 'Preview'] : ['Basics', 'Details', 'Media', 'Preview'];
  
  // Get current step name for conditional rendering
  const currentStepName = steps[currentStep];
  
  // Load existing profile data
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/profiles/${profileId}`, { withCredentials: true });
        const profile = response.data;
        
        setFormData({
          company_name: profile.company_name || '',
          tagline: profile.tagline || '',
          year_established: profile.year_established || '',
          headquarters: profile.headquarters || '',
          products_services: profile.products_services || '',
          key_markets: profile.key_markets || '',
          looking_for: profile.looking_for || '',
          certifications: profile.certifications || [],
          
          // KPI Stats
          products_count: profile.products_count || '',
          clients_count: profile.clients_count || '',
          countries_count: profile.countries_count || '',
          
          // Premium fields
          profile_category: profile.profile_category || 'general',
          custom_category: profile.custom_category || '',
          target_audience: profile.target_audience || '',
          key_message: profile.key_message || '',
          profile_sections: profile.profile_sections || null,
          audience_messages: profile.audience_messages || [],
          
          logo_url: profile.logo_url || '',
          hero_video: profile.hero_video || null,
          additional_videos: profile.additional_videos || [],
          contact_name: profile.contact_name || '',
          contact_designation: profile.contact_designation || '',
          contact_email: profile.contact_email || '',
          contact_phone: profile.contact_phone || '',
          website: profile.website || '',
          include_key_contact: !!profile.key_contact,
          key_contact: profile.key_contact || {
            photo_url: '',
            years_experience: '',
            expertise_areas: [],
            achievements: '',
            linkedin_url: '',
            bio_text: '',
            videos: []
          },
          created_on_behalf_of: profile.created_on_behalf_of || '',
          profile_text: profile.profile_text || '',
          
          // Resources link
          resources_enabled: profile.resources_enabled || false,
          resources_link: profile.resources_link || '',
          resources_label: profile.resources_label || 'Company Resources',
          
          // Multi-language translation
          multi_language_enabled: profile.multi_language_enabled || false,
          
          // Social URLs
          social_urls: profile.social_urls || {
            linkedin: '',
            youtube: '',
            twitter: '',
            instagram: ''
          }
        });
        
        // If resources link exists and is enabled, mark as valid
        if (profile.resources_enabled && profile.resources_link) {
          setResourcesLinkValid(true);
        }
        
        // Detect if profile uses custom text (has profile_text but no/empty profile_sections)
        const hasProfileText = profile.profile_text && profile.profile_text.trim().length > 0;
        const hasProfileSections = profile.profile_sections && (
          profile.profile_sections.who_we_are || 
          profile.profile_sections.our_presence ||
          profile.profile_sections.what_we_seek ||
          profile.profile_sections.why_partner
        );
        
        // If profile has text but no sections, enable custom text mode
        if (hasProfileText && !hasProfileSections) {
          setUseCustomText(true);
        }
        
        // Store profile data for showcase manager
        setProfileData(profile);
      } catch (err) {
        console.error('Failed to load profile:', err);
        setError('Failed to load profile. You may not have permission to edit this profile.');
      } finally {
        setIsLoading(false);
      }
    };
    
    if (profileId && user) {
      fetchProfile();
    }
  }, [profileId, user]);
  
  // Fetch testimonials
  useEffect(() => {
    const fetchTestimonials = async () => {
      if (!profileId) return;
      try {
        const response = await axios.get(`${API_URL}/api/profiles/${profileId}/testimonials`, { withCredentials: true });
        setTestimonials(response.data.testimonials || []);
      } catch (err) {
        console.log('No testimonials found');
      }
    };
    
    if (profileId) {
      fetchTestimonials();
    }
  }, [profileId]);
  
  // Fetch invitations
  useEffect(() => {
    const fetchInvitations = async () => {
      if (!profileId) return;
      try {
        const response = await axios.get(`${API_URL}/api/profiles/${profileId}/invitations`, { withCredentials: true });
        setInvitations(response.data.invitations || []);
      } catch (err) {
        console.log('No invitations found');
      }
    };
    
    if (profileId && canUseInvitations) {
      fetchInvitations();
    }
  }, [profileId, canUseInvitations]);
  
  useEffect(() => {
    if (!profileId) return;
    axios.get(`${API_URL}/api/profiles/${profileId}/features`, { withCredentials: true })
      .then((r) => setFeatures(r.data.features || {}))
      .catch(() => setFeatures({}));
  }, [profileId, subscriptionStatus]);
  
  // Fetch subscription status and analytics
  useEffect(() => {
    const fetchSubscriptionAndAnalytics = async () => {
      if (!profileId) return;
      try {
        // Fetch profile to get subscription status
        const profileRes = await axios.get(`${API_URL}/api/profiles/${profileId}`, { withCredentials: true });
        const profile = profileRes.data;
        setSubscriptionStatus(profile.subscription_status || 'inactive');
        setActiveUntil(profile.active_until ? new Date(profile.active_until) : null);
        
        // Fetch analytics
        const analyticsRes = await axios.get(`${API_URL}/api/profiles/${profileId}/analytics`, { withCredentials: true });
        setAnalytics(analyticsRes.data);
      } catch (err) {
        console.log('Could not fetch subscription/analytics');
      }
    };
    
    if (profileId) {
      fetchSubscriptionAndAnalytics();
    }
  }, [profileId]);
  
  // Certification options
  const certificationOptions = [
    'WHO-GMP', 'ISO 9001', 'ISO 14001', 'ISO 22000', 'FDA Approved',
    'CE Marked', 'HACCP', 'GMP', 'NABL', 'BIS', 'FSSAI', 'Halal', 'Kosher'
  ];
  
  // Expertise area options
  const expertiseOptions = [
    'International Trade', 'Export Management', 'Business Development', 'Strategic Partnerships',
    'Regulatory Compliance', 'Supply Chain', 'Manufacturing', 'Quality Assurance',
    'R&D', 'Sales & Marketing', 'Finance', 'Operations', 'Technology', 'Human Resources'
  ];
  
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };
  
  // Regenerate AI content based on current inputs
  const handleRegenerateAI = async () => {
    // Confirmation dialog to prevent accidental overwrites
    const hasExistingContent = formData.profile_sections && (
      formData.profile_sections.who_we_are || 
      formData.profile_sections.our_presence || 
      formData.profile_sections.what_we_seek ||
      formData.profile_sections.why_partner
    );
    
    if (hasExistingContent) {
      const confirmed = window.confirm(
        '⚠️ This will replace your current profile text with new AI-generated content.\n\n' +
        'Any manual edits you\'ve made will be lost.\n\n' +
        'Are you sure you want to regenerate?'
      );
      if (!confirmed) return;
    }
    
    if (!formData.target_audience.trim() || !formData.key_message.trim()) {
      setError('Please fill in Target Audience and Key Message before regenerating AI content');
      return;
    }
    
    setIsRegeneratingAI(true);
    setError('');
    
    try {
      const response = await axios.post(`${API_URL}/api/profiles/generate-structured`, {
        company_name: formData.company_name,
        tagline: formData.tagline,
        products_services: formData.products_services,
        key_markets: formData.key_markets,
        looking_for: formData.looking_for,
        profile_category: formData.profile_category,
        custom_category: formData.custom_category,
        target_audience: formData.target_audience,
        key_message: formData.key_message
      }, { withCredentials: true });
      
      setFormData(prev => ({
        ...prev,
        profile_sections: response.data.profile_sections,
        audience_messages: response.data.audience_messages || [],
        profile_text: response.data.profile_text || prev.profile_text
      }));
    } catch (err) {
      console.error('Error regenerating AI content:', err);
      setError('Failed to regenerate AI content. Please try again.');
    } finally {
      setIsRegeneratingAI(false);
    }
  };
  
  const toggleCertification = (cert) => {
    setFormData(prev => ({
      ...prev,
      certifications: prev.certifications.includes(cert)
        ? prev.certifications.filter(c => c !== cert)
        : [...prev.certifications, cert]
    }));
  };
  
  const toggleExpertise = (expertise) => {
    setFormData(prev => ({
      ...prev,
      key_contact: {
        ...prev.key_contact,
        expertise_areas: prev.key_contact.expertise_areas.includes(expertise)
          ? prev.key_contact.expertise_areas.filter(e => e !== expertise)
          : [...prev.key_contact.expertise_areas, expertise]
      }
    }));
  };
  
  const updateKeyContact = (field, value) => {
    setFormData(prev => ({
      ...prev,
      key_contact: { ...prev.key_contact, [field]: value }
    }));
  };
  
  const addKeyContactVideo = () => {
    if (formData.key_contact.videos.length < 3) {
      setFormData(prev => ({
        ...prev,
        key_contact: {
          ...prev.key_contact,
          videos: [...prev.key_contact.videos, { type: 'youtube', url: '', title: '' }]
        }
      }));
    }
  };
  
  const updateKeyContactVideo = (index, field, value) => {
    setFormData(prev => {
      const videos = [...prev.key_contact.videos];
      videos[index] = { ...videos[index], [field]: value };
      return { ...prev, key_contact: { ...prev.key_contact, videos } };
    });
  };
  
  const removeKeyContactVideo = (index) => {
    setFormData(prev => ({
      ...prev,
      key_contact: {
        ...prev.key_contact,
        videos: prev.key_contact.videos.filter((_, i) => i !== index)
      }
    }));
  };
  
  const handleContactPhotoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file (PNG, JPG, etc.)');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      setError('Photo file must be less than 5MB');
      return;
    }
    
    setIsUploadingContactPhoto(true);
    setError('');
    
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      formDataUpload.append('upload_preset', 'ml_default');
      
      const response = await axios.post(
        `https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || 'demo'}/image/upload`,
        formDataUpload
      );
      
      if (response.data.secure_url) {
        updateKeyContact('photo_url', response.data.secure_url);
      }
    } catch (err) {
      console.error('Photo upload failed:', err);
      const reader = new FileReader();
      reader.onloadend = () => {
        updateKeyContact('photo_url', reader.result);
      };
      reader.readAsDataURL(file);
    } finally {
      setIsUploadingContactPhoto(false);
    }
  };
  
  const removeContactPhoto = () => {
    updateKeyContact('photo_url', '');
    if (contactPhotoInputRef.current) {
      contactPhotoInputRef.current.value = '';
    }
  };
  
  const generateContactBio = async () => {
    // Confirmation dialog if there's existing bio
    if (formData.key_contact.bio_text && formData.key_contact.bio_text.trim().length > 30) {
      const confirmed = window.confirm(
        '⚠️ This will replace the current bio with new AI-generated content.\n\n' +
        'Any manual edits will be lost.\n\n' +
        'Are you sure you want to regenerate?'
      );
      if (!confirmed) return;
    }
    
    setIsGeneratingContactBio(true);
    setError('');
    
    try {
      const response = await axios.post(`${API_URL}/api/profiles/generate-contact-bio`, {
        name: formData.contact_name,
        designation: formData.contact_designation,
        company_name: formData.company_name,
        years_experience: formData.key_contact.years_experience,
        expertise_areas: formData.key_contact.expertise_areas.join(', '),
        achievements: formData.key_contact.achievements
      }, { withCredentials: true });
      
      updateKeyContact('bio_text', response.data.bio_text);
    } catch (err) {
      setError('Failed to generate bio. Please try again.');
      console.error(err);
    } finally {
      setIsGeneratingContactBio(false);
    }
  };
  
  const generateProfileText = async (skipConfirmation = false) => {
    // Confirmation dialog if there's existing text
    if (!skipConfirmation && formData.profile_text && formData.profile_text.trim().length > 50) {
      const confirmed = window.confirm(
        '⚠️ This will replace your current profile description with new AI-generated content.\n\n' +
        'Any manual edits you\'ve made will be lost.\n\n' +
        'Are you sure you want to regenerate?'
      );
      if (!confirmed) return;
    }
    
    setIsGenerating(true);
    setError('');
    
    try {
      const response = await axios.post(`${API_URL}/api/profiles/generate-text`, {
        company_name: formData.company_name,
        tagline: formData.tagline,
        year_established: formData.year_established,
        headquarters: formData.headquarters,
        products_services: formData.products_services,
        key_markets: formData.key_markets,
        looking_for: formData.looking_for,
        certifications: formData.certifications.join(', '),
        contact_name: formData.contact_name,
        contact_designation: formData.contact_designation,
        contact_email: formData.contact_email,
        contact_phone: formData.contact_phone
      }, { withCredentials: true });
      
      setFormData(prev => ({ ...prev, profile_text: response.data.profile_text }));
    } catch (err) {
      setError('Failed to generate profile text. Please try again.');
      console.error(err);
    } finally {
      setIsGenerating(false);
    }
  };
  
  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file (PNG, JPG, etc.)');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      setError('Logo file must be less than 5MB');
      return;
    }
    
    setIsUploadingLogo(true);
    setError('');
    
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      formDataUpload.append('upload_preset', 'ml_default');
      
      const response = await axios.post(
        `https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || 'demo'}/image/upload`,
        formDataUpload
      );
      
      if (response.data.secure_url) {
        setFormData(prev => ({ ...prev, logo_url: response.data.secure_url }));
      }
    } catch (err) {
      console.error('Logo upload failed:', err);
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({ ...prev, logo_url: reader.result }));
      };
      reader.readAsDataURL(file);
    } finally {
      setIsUploadingLogo(false);
    }
  };
  
  const removeLogo = () => {
    setFormData(prev => ({ ...prev, logo_url: '' }));
    if (logoInputRef.current) {
      logoInputRef.current.value = '';
    }
  };
  
  // Open signature customization modal
  const openSignatureModal = () => {
    setSignatureData({
      name: formData.contact_name || '',
      designation: formData.contact_designation || '',
      email: formData.contact_email || '',
      phone: formData.contact_phone || ''
    });
    setShowSignatureModal(true);
  };
  
  // Download Email Signature Strip
  const downloadEmailSignature = async () => {
    setIsGeneratingSignature(true);
    
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    // Signature strip dimensions (600x160 for email-friendly size)
    const width = 600;
    const height = 160;
    canvas.width = width;
    canvas.height = height;
    
    // Background - dark theme matching BrandStage
    ctx.fillStyle = '#18181B';
    ctx.fillRect(0, 0, width, height);
    
    // Red accent line at top
    ctx.fillStyle = '#C41E3A';
    ctx.fillRect(0, 0, width, 4);
    
    // Company logo area (left side)
    const logoSize = 70;
    const logoX = 20;
    const logoY = (height - logoSize) / 2 + 2;
    
    const drawSignatureContent = () => {
      // Contact details section
      const textX = logoX + logoSize + 20;
      let textY = 28;
      
      // Company name (bold, white)
      ctx.fillStyle = '#FFFFFF';
      ctx.font = 'bold 16px Arial, sans-serif';
      ctx.textAlign = 'left';
      ctx.fillText(formData.company_name || 'Company Name', textX, textY);
      
      // Contact name with designation
      textY += 20;
      ctx.fillStyle = '#E4E4E7';
      ctx.font = '13px Arial, sans-serif';
      if (signatureData.name && signatureData.designation) {
        ctx.fillText(`${signatureData.name}, ${signatureData.designation}`, textX, textY);
        textY += 18;
      } else if (signatureData.name) {
        ctx.fillText(signatureData.name, textX, textY);
        textY += 18;
      }
      
      // Email
      ctx.fillStyle = '#A1A1AA';
      ctx.font = '11px Arial, sans-serif';
      if (signatureData.email) {
        ctx.fillText(`✉  ${signatureData.email}`, textX, textY);
        textY += 15;
      }
      
      // Phone
      if (signatureData.phone) {
        ctx.fillText(`📞  ${signatureData.phone}`, textX, textY);
        textY += 15;
      }
      
      // Location
      if (formData.headquarters) {
        ctx.fillText(`📍  ${formData.headquarters}`, textX, textY);
        textY += 15;
      }
      
      // Website
      if (formData.website) {
        const displayWebsite = formData.website.replace(/^https?:\/\//, '');
        ctx.fillText(`🌐  ${displayWebsite}`, textX, textY);
      }
      
      // Right side - Profile URL info
      const profileUrl = `brandstage-media.com/p/${profileId}`;
      ctx.fillStyle = '#71717A';
      ctx.font = '9px Arial, sans-serif';
      ctx.textAlign = 'right';
      ctx.fillText('View Company Profile:', width - 20, height - 25);
      ctx.fillStyle = '#C41E3A';
      ctx.fillText(profileUrl, width - 20, height - 12);
      
      // Download
      const pngUrl = canvas.toDataURL('image/png');
      const downloadLink = document.createElement('a');
      downloadLink.download = `${profileId || 'company'}-email-signature.png`;
      downloadLink.href = pngUrl;
      downloadLink.click();
      
      setIsGeneratingSignature(false);
      setShowSignatureModal(false);
    };
    
    // Try to load company logo
    if (formData.logo_url) {
      const logoImg = new Image();
      logoImg.crossOrigin = 'anonymous';
      logoImg.onload = () => {
        // Draw logo with white background
        ctx.fillStyle = '#FFFFFF';
        ctx.beginPath();
        ctx.roundRect(logoX, logoY, logoSize, logoSize, 8);
        ctx.fill();
        
        // Calculate aspect ratio to fit logo
        const scale = Math.min(logoSize / logoImg.width, logoSize / logoImg.height) * 0.8;
        const scaledWidth = logoImg.width * scale;
        const scaledHeight = logoImg.height * scale;
        const offsetX = logoX + (logoSize - scaledWidth) / 2;
        const offsetY = logoY + (logoSize - scaledHeight) / 2;
        
        ctx.drawImage(logoImg, offsetX, offsetY, scaledWidth, scaledHeight);
        drawSignatureContent();
      };
      logoImg.onerror = () => {
        // If logo fails to load, draw initials
        ctx.fillStyle = '#C41E3A';
        ctx.beginPath();
        ctx.roundRect(logoX, logoY, logoSize, logoSize, 8);
        ctx.fill();
        ctx.fillStyle = '#FFFFFF';
        ctx.font = 'bold 28px Arial, sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText((formData.company_name || 'C').charAt(0), logoX + logoSize/2, logoY + logoSize/2);
        ctx.textBaseline = 'alphabetic';
        drawSignatureContent();
      };
      logoImg.src = formData.logo_url;
    } else {
      // No logo - draw company initial
      ctx.fillStyle = '#C41E3A';
      ctx.beginPath();
      ctx.roundRect(logoX, logoY, logoSize, logoSize, 8);
      ctx.fill();
      ctx.fillStyle = '#FFFFFF';
      ctx.font = 'bold 28px Arial, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText((formData.company_name || 'C').charAt(0), logoX + logoSize/2, logoY + logoSize/2);
      ctx.textBaseline = 'alphabetic';
      drawSignatureContent();
    }
  };
  
  const addVideoSlot = () => {
    if (formData.additional_videos.length < 5) {
      setFormData(prev => ({
        ...prev,
        additional_videos: [...prev.additional_videos, { type: 'youtube', url: '', title: '' }]
      }));
    }
  };
  
  const updateVideo = (index, field, value) => {
    setFormData(prev => {
      const videos = [...prev.additional_videos];
      videos[index] = { ...videos[index], [field]: value };
      return { ...prev, additional_videos: videos };
    });
  };
  
  const removeVideo = (index) => {
    setFormData(prev => ({
      ...prev,
      additional_videos: prev.additional_videos.filter((_, i) => i !== index)
    }));
  };
  
  // Validate Google Drive/Docs link
  const validateResourcesLink = async (url) => {
    if (!url || !url.trim()) {
      setResourcesLinkValid(null);
      return false;
    }
    
    // Check if it's a valid Google URL format
    const googleUrlPatterns = [
      /^https:\/\/(drive|docs)\.google\.com\//,
      /^https:\/\/docs\.google\.com\/(document|spreadsheets|presentation|forms)/,
      /^https:\/\/drive\.google\.com\/(drive\/folders|file\/d|open)/
    ];
    
    const isGoogleUrl = googleUrlPatterns.some(pattern => pattern.test(url));
    
    if (!isGoogleUrl) {
      setResourcesLinkValid(false);
      return false;
    }
    
    setResourcesLinkChecking(true);
    
    try {
      // Use backend to validate the link is accessible
      const response = await axios.post(`${API_URL}/api/validate-url`, { url }, { withCredentials: true });
      const isValid = response.data.valid;
      setResourcesLinkValid(isValid);
      return isValid;
    } catch (err) {
      console.error('Error validating URL:', err);
      // If validation endpoint fails, check format only
      setResourcesLinkValid(isGoogleUrl);
      return isGoogleUrl;
    } finally {
      setResourcesLinkChecking(false);
    }
  };

  // Helper to normalize API error messages (handles 422 array errors)
  const getApiErrorMessage = (err) => {
    const detail = err.response?.data?.detail;
    if (!detail) return 'An error occurred';
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      return detail.map(d => d.msg || d.message || JSON.stringify(d)).join(', ');
    }
    return 'An error occurred';
  };
  
  // Testimonial functions
  const requestTestimonial = async () => {
    if (!testimonialRequest.contact_name || !testimonialRequest.contact_email) {
      setError('Contact name and email are required');
      return;
    }
    
    setIsRequestingTestimonial(true);
    try {
      await axios.post(
        `${API_URL}/api/profiles/${profileId}/request-testimonial`,
        testimonialRequest,
        { withCredentials: true }
      );
      
      // Refresh testimonials list
      const response = await axios.get(`${API_URL}/api/profiles/${profileId}/testimonials`, { withCredentials: true });
      setTestimonials(response.data.testimonials || []);
      
      setShowTestimonialModal(false);
      setTestimonialRequest({ contact_name: '', contact_email: '', message: '' });
      setError('');
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setIsRequestingTestimonial(false);
    }
  };
  
  const toggleTestimonialVisibility = async (testimonialId) => {
    setTogglingVisibility(testimonialId);
    try {
      const response = await axios.put(
        `${API_URL}/api/testimonials/${testimonialId}/visibility`,
        {},
        { withCredentials: true }
      );
      
      // Update local state
      setTestimonials(prev => prev.map(t => 
        t.id === testimonialId ? { ...t, is_visible: response.data.is_visible } : t
      ));
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setTogglingVisibility(null);
    }
  };
  
  // Invitation functions
  const createInvitation = async () => {
    if (!invitationForm.event_title || !invitationForm.event_date || !invitationForm.venue || !invitationForm.description) {
      setError('Event title, date, venue, and description are required');
      return;
    }
    
    setIsCreatingInvitation(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/profiles/${profileId}/invitations`,
        invitationForm,
        { withCredentials: true }
      );
      
      // Refresh invitations list
      const invRes = await axios.get(`${API_URL}/api/profiles/${profileId}/invitations`, { withCredentials: true });
      setInvitations(invRes.data.invitations || []);
      
      setShowInvitationModal(false);
      setInvitationForm({
        event_title: '',
        event_type: 'seminar',
        event_date: '',
        event_time: '',
        venue: '',
        description: '',
        objectives: '',
        banner_image_url: ''
      });
      setError('');
      
      // Show success with share URL
      alert(`Invitation created! Share URL: ${window.location.origin}${response.data.share_url}`);
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setIsCreatingInvitation(false);
    }
  };
  
  const deleteInvitation = async (invitationId) => {
    if (!window.confirm('Are you sure you want to delete this invitation?')) return;
    
    try {
      await axios.delete(
        `${API_URL}/api/profiles/${profileId}/invitations/${invitationId}`,
        { withCredentials: true }
      );
      setInvitations(prev => prev.filter(inv => inv.id !== invitationId));
    } catch (err) {
      setError(getApiErrorMessage(err));
    }
  };
  
  const copyInvitationLink = (shareUrl) => {
    navigator.clipboard.writeText(`${window.location.origin}${shareUrl}`);
    alert('Invitation link copied to clipboard!');
  };
  
  // Coupon validation
  const validateCoupon = async () => {
    if (!couponCode.trim()) return;
    
    setIsValidatingCoupon(true);
    setCouponValidation(null);
    try {
      const response = await axios.post(`${API_URL}/api/coupons/validate?coupon_code=${couponCode.toUpperCase()}&plan=6_months`);
      setCouponValidation(response.data);
    } catch (err) {
      setCouponValidation({ 
        valid: false, 
        error: err.response?.data?.detail || 'Invalid coupon code' 
      });
    } finally {
      setIsValidatingCoupon(false);
    }
  };
  
  // Apply coupon to activate subscription
  const applyCoupon = async () => {
    if (!couponValidation?.valid) return;
    
    setIsApplyingCoupon(true);
    try {
      const response = await axios.post(`${API_URL}/api/coupons/apply`, {
        coupon_code: couponCode.toUpperCase(),
        profile_id: profileId,
        profile_type: 'company'
      }, { withCredentials: true });
      
      // Update local subscription status — this triggers feature re-fetch
      setSubscriptionStatus('active');
      setActiveUntil(new Date(response.data.subscription.active_until));
      setCouponCode('');
      setCouponValidation(null);
      
      // Explicitly re-fetch features with a small delay to ensure DB is updated
      setTimeout(() => {
        axios.get(`${API_URL}/api/profiles/${profileId}/features`, { withCredentials: true })
          .then((r) => setFeatures(r.data.features || {}))
          .catch(() => {});
      }, 500);
      
      // Show success with clear guidance
      setError('');
      alert('Profile activated with All-Access! All features are now unlocked. The page will refresh to apply changes.');
      window.location.reload();
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setIsApplyingCoupon(false);
    }
  };
  
  // Calculate days until expiry
  const getDaysUntilExpiry = () => {
    if (!activeUntil) return null;
    const now = new Date();
    const diff = activeUntil - now;
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  };
  
  const daysUntilExpiry = getDaysUntilExpiry();
  
  const validateStep = () => {
    switch (currentStep) {
      case 0:
        if (!formData.company_name.trim()) {
          setError('Company name is required');
          return false;
        }
        break;
      case 1:
        if (!formData.products_services.trim()) {
          setError('Products/Services description is required');
          return false;
        }
        if (!formData.contact_name.trim() || !formData.contact_email.trim()) {
          setError('Contact name and email are required');
          return false;
        }
        break;
      case 2:
        break;
      case 3:
        if (!formData.profile_text.trim()) {
          setError('Please generate or write profile text');
          return false;
        }
        break;
      default:
        break;
    }
    setError('');
    return true;
  };
  
  const nextStep = async () => {
    if (!validateStep()) return;
    
    // Generate profile text before Preview step (Details -> Media transition for non-premium, or Media step for premium)
    // Skip confirmation since this only runs when text is empty
    const mediaStepIndex = steps.indexOf('Media');
    if (currentStep === mediaStepIndex && !formData.profile_text) {
      await generateProfileText(true); // skipConfirmation = true
    }
    
    setCurrentStep(prev => Math.min(prev + 1, steps.length - 1));
  };
  
  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 0));
  };
  
  const saveProfile = async () => {
    if (!validateStep()) return;
    
    // Validate resources link if enabled
    if (formData.resources_enabled && formData.resources_link) {
      const isLinkValid = await validateResourcesLink(formData.resources_link);
      if (!isLinkValid) {
        setError('The Google Drive/Docs link appears to be invalid or inaccessible. Please check the URL and ensure it is publicly shared.');
        return;
      }
    }
    
    setIsSubmitting(true);
    setError('');
    
    try {
      const validVideos = formData.additional_videos.filter(v => v.url.trim());
      const validKeyContactVideos = formData.key_contact.videos.filter(v => v.url.trim());
      
      const keyContactData = formData.include_key_contact ? {
        photo_url: formData.key_contact.photo_url,
        years_experience: formData.key_contact.years_experience,
        expertise_areas: formData.key_contact.expertise_areas,
        achievements: formData.key_contact.achievements,
        linkedin_url: formData.key_contact.linkedin_url,
        bio_text: formData.key_contact.bio_text,
        videos: validKeyContactVideos
      } : null;
      
      // If using custom text mode, clear profile_sections so public profile shows the custom text
      const profileSectionsToSave = useCustomText ? null : formData.profile_sections;
      
      await axios.put(`${API_URL}/api/profiles/${profileId}`, {
        company_name: formData.company_name,
        tagline: formData.tagline,
        logo_url: formData.logo_url,
        profile_text: formData.profile_text,
        year_established: formData.year_established,
        headquarters: formData.headquarters,
        products_services: formData.products_services,
        key_markets: formData.key_markets,
        looking_for: formData.looking_for,
        certifications: formData.certifications,
        
        // KPI Stats
        products_count: formData.products_count ? parseInt(formData.products_count) : null,
        clients_count: formData.clients_count ? parseInt(formData.clients_count) : null,
        countries_count: formData.countries_count || null,  // Can be number or text
        
        // Premium fields
        profile_category: formData.profile_category,
        custom_category: formData.custom_category,
        target_audience: formData.target_audience,
        key_message: formData.key_message,
        profile_sections: profileSectionsToSave,  // null if custom text mode
        audience_messages: formData.audience_messages,
        
        hero_video: formData.hero_video,
        additional_videos: validVideos,
        contact_name: formData.contact_name,
        contact_designation: formData.contact_designation,
        contact_email: formData.contact_email,
        contact_phone: formData.contact_phone,
        website: formData.website,
        created_on_behalf_of: formData.created_on_behalf_of,
        key_contact: keyContactData,
        
        // Resources link
        resources_enabled: formData.resources_enabled,
        resources_link: formData.resources_enabled ? formData.resources_link : '',
        resources_label: formData.resources_label || 'Company Resources',
        
        // Multi-language translation
        multi_language_enabled: formData.multi_language_enabled,
        
        // Social URLs
        social_urls: formData.social_urls
      }, { withCredentials: true });
      
      // Navigate back to public profile
      const profileResponse = await axios.get(`${API_URL}/api/profiles/${profileId}`, { withCredentials: true });
      navigate(`/p/${profileResponse.data.slug}`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save profile. Please try again.');
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#C41E3A] animate-spin" />
      </div>
    );
  }
  
  // Redirect if not logged in
  if (!user) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center p-6">
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-8 max-w-md text-center">
          <Building2 className="w-12 h-12 text-[#C41E3A] mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Login Required</h2>
          <p className="text-zinc-400 mb-6">Please log in to edit your company profile.</p>
          <button
            onClick={() => navigate('/login')}
            className="bg-[#C41E3A] hover:bg-[#A01830] text-white px-6 py-3 rounded-lg font-semibold"
          >
            Log In
          </button>
        </div>
      </div>
    );
  }
  
  // Error loading profile
  if (error && isLoading === false && !formData.company_name) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center p-6">
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-8 max-w-md text-center">
          <Building2 className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Cannot Edit Profile</h2>
          <p className="text-zinc-400 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="bg-[#C41E3A] hover:bg-[#A01830] text-white px-6 py-3 rounded-lg font-semibold"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-[#09090B] py-12 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <p className="text-xs tracking-[0.3em] uppercase text-[#C41E3A] font-bold mb-2">
            Edit Profile
          </p>
          <h1 className="text-3xl md:text-4xl font-black text-white tracking-tight">
            Update Your Business Profile
          </h1>
          <p className="text-zinc-400 mt-2">
            Make changes to your company profile
          </p>
        </div>
        
        {/* Subscription Expiry Alert */}
        {subscriptionStatus === 'active' && daysUntilExpiry !== null && daysUntilExpiry <= 30 && (
          <div className={`mb-6 rounded-lg p-4 flex items-center justify-between ${
            daysUntilExpiry <= 0 
              ? 'bg-red-900/30 border border-red-800' 
              : daysUntilExpiry <= 7 
              ? 'bg-red-900/20 border border-red-700' 
              : 'bg-amber-900/20 border border-amber-700'
          }`} data-testid="subscription-expiry-alert">
            <div className="flex items-center gap-3">
              <AlertTriangle className={`w-5 h-5 ${daysUntilExpiry <= 7 ? 'text-red-400' : 'text-amber-400'}`} />
              <div>
                <p className={`font-medium ${daysUntilExpiry <= 7 ? 'text-red-400' : 'text-amber-400'}`}>
                  {daysUntilExpiry <= 0 
                    ? 'Your profile subscription has expired!' 
                    : daysUntilExpiry <= 7 
                    ? `Urgent: Your profile expires in ${daysUntilExpiry} day${daysUntilExpiry === 1 ? '' : 's'}!`
                    : `Your profile expires in ${daysUntilExpiry} days`}
                </p>
                <p className="text-zinc-400 text-sm">
                  {daysUntilExpiry <= 0 
                    ? 'Your profile is no longer visible to the public. Renew to make it live again.'
                    : 'Renew your subscription to keep your profile live and visible.'}
                </p>
              </div>
            </div>
            <button
              onClick={() => setCurrentStep(steps.length - 1)} // Go to Preview step with coupon input
              className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap ${
                daysUntilExpiry <= 7 
                  ? 'bg-red-600 hover:bg-red-500 text-white' 
                  : 'bg-amber-600 hover:bg-amber-500 text-white'
              }`}
            >
              Renew Now
            </button>
          </div>
        )}
        
        {subscriptionStatus === 'inactive' && (
          <div className="mb-6 bg-blue-900/20 border border-blue-700 rounded-lg p-4 flex items-center justify-between" data-testid="subscription-inactive-alert">
            <div className="flex items-center gap-3">
              <Gift className="w-5 h-5 text-blue-400" />
              <div>
                <p className="font-medium text-blue-400">Profile Not Yet Live</p>
                <p className="text-zinc-400 text-sm">Activate your profile with a promo code to make it visible to the public.</p>
              </div>
            </div>
            <button
              onClick={() => setCurrentStep(steps.length - 1)} // Go to Preview step with coupon input
              className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-lg font-medium whitespace-nowrap"
            >
              Activate Profile
            </button>
          </div>
        )}
        
        {/* Analytics Dashboard (if active and has data) */}
        {analytics && subscriptionStatus === 'active' && (
          <div className="mb-6 bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden" data-testid="analytics-dashboard">
            {/* Header */}
            <div className="bg-gradient-to-r from-[#C41E3A]/20 to-zinc-900 px-5 py-4 border-b border-zinc-800">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-[#C41E3A]" />
                  <span className="text-white font-semibold">Profile Analytics</span>
                </div>
                <span className="text-zinc-500 text-xs">
                  Active until {activeUntil?.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                </span>
              </div>
            </div>
            
            {/* Main Stats Grid */}
            <div className="p-5">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                {/* Total Views */}
                <div className="bg-zinc-800/50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Eye className="w-4 h-4 text-blue-400" />
                    <span className="text-zinc-400 text-xs">Profile Views</span>
                  </div>
                  <div className="text-2xl font-bold text-white">{analytics.views?.total || 0}</div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-zinc-500">Today: {analytics.views?.today || 0}</span>
                    {analytics.views?.this_week > 0 && (
                      <span className="text-xs text-emerald-400 flex items-center gap-0.5">
                        <TrendingUp className="w-3 h-3" />
                        {analytics.views?.this_week} this week
                      </span>
                    )}
                  </div>
                </div>
                
                {/* Contact Clicks */}
                <div className="bg-zinc-800/50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <MousePointer className="w-4 h-4 text-green-400" />
                    <span className="text-zinc-400 text-xs">Contact Clicks</span>
                  </div>
                  <div className="text-2xl font-bold text-white">{analytics.contact_clicks?.total || 0}</div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-zinc-500">Today: {analytics.contact_clicks?.today || 0}</span>
                    {analytics.contact_clicks?.this_week > 0 && (
                      <span className="text-xs text-emerald-400 flex items-center gap-0.5">
                        <TrendingUp className="w-3 h-3" />
                        {analytics.contact_clicks?.this_week} this week
                      </span>
                    )}
                  </div>
                </div>
                
                {/* Video Plays */}
                <div className="bg-zinc-800/50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Play className="w-4 h-4 text-purple-400" />
                    <span className="text-zinc-400 text-xs">Video Plays</span>
                  </div>
                  <div className="text-2xl font-bold text-white">{analytics.video_plays?.total || 0}</div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-zinc-500">Today: {analytics.video_plays?.today || 0}</span>
                    {analytics.video_plays?.this_week > 0 && (
                      <span className="text-xs text-emerald-400 flex items-center gap-0.5">
                        <TrendingUp className="w-3 h-3" />
                        {analytics.video_plays?.this_week} this week
                      </span>
                    )}
                  </div>
                </div>
                
                {/* Share Clicks */}
                <div className="bg-zinc-800/50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Share2 className="w-4 h-4 text-amber-400" />
                    <span className="text-zinc-400 text-xs">Profile Shares</span>
                  </div>
                  <div className="text-2xl font-bold text-white">{analytics.share_clicks?.total || 0}</div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-zinc-500">Today: {analytics.share_clicks?.today || 0}</span>
                    {analytics.share_clicks?.this_week > 0 && (
                      <span className="text-xs text-emerald-400 flex items-center gap-0.5">
                        <TrendingUp className="w-3 h-3" />
                        {analytics.share_clicks?.this_week} this week
                      </span>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Period Summary */}
              <div className="bg-zinc-800/30 rounded-lg p-4">
                <h4 className="text-sm font-medium text-zinc-300 mb-3">30-Day Summary</h4>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="text-lg font-semibold text-white">{analytics.views?.this_month || 0}</div>
                    <div className="text-xs text-zinc-500">Views</div>
                  </div>
                  <div>
                    <div className="text-lg font-semibold text-white">{analytics.contact_clicks?.this_month || 0}</div>
                    <div className="text-xs text-zinc-500">Contact Clicks</div>
                  </div>
                  <div>
                    <div className="text-lg font-semibold text-white">{analytics.video_plays?.this_month || 0}</div>
                    <div className="text-xs text-zinc-500">Video Plays</div>
                  </div>
                </div>
              </div>
              
              {/* Engagement Rate - if there are views */}
              {(analytics.views?.total || 0) > 0 && (
                <div className="mt-4 flex items-center justify-between text-sm">
                  <span className="text-zinc-400">Engagement Rate</span>
                  <span className="text-white font-medium">
                    {(((analytics.contact_clicks?.total || 0) / (analytics.views?.total || 1)) * 100).toFixed(1)}%
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Profile Score Widget - Always visible */}
        <div className="mb-6">
          <ProfileScoreWidget 
            profileId={profileId}
            onSuggestionClick={(suggestion) => {
              if (suggestion.action === 'scroll_to_social') {
                document.getElementById('social-urls-section')?.scrollIntoView({ behavior: 'smooth' });
              } else if (suggestion.action === 'scroll_to_key_contact') {
                const detailsIndex = steps.indexOf('Details');
                if (detailsIndex >= 0) setCurrentStep(detailsIndex);
              } else if (suggestion.link) {
                window.location.href = suggestion.link;
              }
            }}
          />
        </div>
        
        {/* Step Indicator */}
        <StepIndicator currentStep={currentStep} steps={steps} />
        
        {/* Error Message */}
        {error && (
          <div className="bg-red-900/30 border border-red-800 text-red-400 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}
        
        {/* Form Steps */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 md:p-8">
          
          {/* Step: Basics */}
          {currentStepName === 'Basics' && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <Building2 className="w-5 h-5 text-[#C41E3A]" />
                Company Basics
              </h2>
              
              <div>
                <label className="block text-zinc-300 text-sm font-medium mb-2">
                  Company Name *
                </label>
                <input
                  type="text"
                  value={formData.company_name}
                  onChange={(e) => handleInputChange('company_name', e.target.value)}
                  placeholder="e.g., Meridian Pharmaceuticals"
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50 focus:border-[#C41E3A]"
                />
              </div>
              
              <div>
                <label className="block text-zinc-300 text-sm font-medium mb-2">
                  Tagline
                </label>
                <input
                  type="text"
                  value={formData.tagline}
                  onChange={(e) => handleInputChange('tagline', e.target.value)}
                  placeholder="e.g., Excellence in API Manufacturing"
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50 focus:border-[#C41E3A]"
                />
              </div>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-zinc-300 text-sm font-medium mb-2">
                    <Calendar className="w-4 h-4 inline mr-1" />
                    Year Established
                  </label>
                  <input
                    type="text"
                    value={formData.year_established}
                    onChange={(e) => handleInputChange('year_established', e.target.value)}
                    placeholder="e.g., 1992"
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50 focus:border-[#C41E3A]"
                  />
                </div>
                <div>
                  <label className="block text-zinc-300 text-sm font-medium mb-2">
                    <MapPin className="w-4 h-4 inline mr-1" />
                    Headquarters
                  </label>
                  <input
                    type="text"
                    value={formData.headquarters}
                    onChange={(e) => handleInputChange('headquarters', e.target.value)}
                    placeholder="e.g., Mumbai, India"
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50 focus:border-[#C41E3A]"
                  />
                </div>
              </div>
              
              {/* Admin-only field */}
              {isAdmin && (
                <div className="border-t border-zinc-700 pt-6 mt-6">
                  <label className="block text-zinc-300 text-sm font-medium mb-2">
                    <Handshake className="w-4 h-4 inline mr-1" />
                    Created on Behalf of (Agency/Trade Mission)
                  </label>
                  <input
                    type="text"
                    value={formData.created_on_behalf_of}
                    onChange={(e) => handleInputChange('created_on_behalf_of', e.target.value)}
                    placeholder="e.g., Ghana Pharma Trade Delegation 2026"
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50 focus:border-[#C41E3A]"
                  />
                  <p className="text-zinc-500 text-xs mt-1">
                    Leave blank if creating for direct client
                  </p>
                </div>
              )}
            </div>
          )}
          
          {/* Step: Objective (Premium Only) */}
          {currentStepName === 'Objective' && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <Target className="w-5 h-5 text-[#C41E3A]" />
                Profile Objective
              </h2>
              <p className="text-zinc-400 text-sm">
                Define your profile&apos;s purpose to generate targeted AI content.
              </p>
              
              {/* Profile Category Selection */}
              <div>
                <label className="block text-zinc-300 text-sm font-medium mb-3">
                  Profile Category
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {profileCategories.map((cat) => (
                    <button
                      key={cat.id}
                      type="button"
                      onClick={() => handleInputChange('profile_category', cat.id)}
                      data-testid={`category-option-${cat.id}`}
                      className={`p-3 rounded-lg border text-left transition-all ${
                        formData.profile_category === cat.id
                          ? 'border-[#C41E3A] bg-[#C41E3A]/10'
                          : 'border-zinc-700 hover:border-zinc-600'
                      }`}
                    >
                      <div className="font-medium text-sm text-white">{cat.label}</div>
                      <div className="text-xs text-zinc-500 mt-1">{cat.description}</div>
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Custom Category */}
              {formData.profile_category === 'other' && (
                <div>
                  <label className="block text-zinc-300 text-sm font-medium mb-2">
                    Custom Category Description *
                  </label>
                  <input
                    type="text"
                    value={formData.custom_category}
                    onChange={(e) => handleInputChange('custom_category', e.target.value)}
                    placeholder="Describe your specific profile purpose..."
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50 focus:border-[#C41E3A]"
                  />
                </div>
              )}
              
              {/* Target Audience */}
              <div>
                <label className="block text-zinc-300 text-sm font-medium mb-2">
                  <MessageSquare className="w-4 h-4 inline mr-1" />
                  Target Audience *
                </label>
                <input
                  type="text"
                  value={formData.target_audience}
                  onChange={(e) => handleInputChange('target_audience', e.target.value)}
                  placeholder="e.g., European pharmaceutical distributors, African healthcare buyers"
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50 focus:border-[#C41E3A]"
                />
              </div>
              
              {/* Key Message */}
              <div>
                <label className="block text-zinc-300 text-sm font-medium mb-2">
                  <Briefcase className="w-4 h-4 inline mr-1" />
                  Key Message *
                </label>
                <textarea
                  value={formData.key_message}
                  onChange={(e) => handleInputChange('key_message', e.target.value)}
                  placeholder="What's the main message you want to convey? e.g., We're expanding to Europe and seeking distribution partners"
                  rows={3}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50 focus:border-[#C41E3A] resize-none"
                />
              </div>
              
              {/* Custom Text vs AI Sections Toggle */}
              <div className="border-t border-zinc-700 pt-6 mt-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                      {useCustomText ? (
                        <>
                          <FileText className="w-5 h-5 text-blue-400" />
                          Custom Profile Text
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-5 h-5 text-[#C41E3A]" />
                          AI-Generated Sections
                        </>
                      )}
                    </h3>
                    <p className="text-zinc-500 text-sm mt-1">
                      {useCustomText 
                        ? "Write your own profile description in your own words" 
                        : "AI generates structured sections based on your inputs"}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      if (!useCustomText && formData.profile_sections) {
                        const confirmed = window.confirm(
                          'Switching to Custom Text will hide the AI-generated sections.\n\n' +
                          'Your custom text will be shown on the public profile instead.\n\n' +
                          'Continue?'
                        );
                        if (!confirmed) return;
                      }
                      setUseCustomText(!useCustomText);
                    }}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg border border-zinc-600 hover:border-zinc-500 transition-colors"
                    data-testid="toggle-custom-text"
                  >
                    {useCustomText ? (
                      <>
                        <ToggleRight className="w-5 h-5 text-blue-400" />
                        <span className="text-sm text-zinc-300">Custom Text ON</span>
                      </>
                    ) : (
                      <>
                        <ToggleLeft className="w-5 h-5 text-zinc-500" />
                        <span className="text-sm text-zinc-400">AI Sections</span>
                      </>
                    )}
                  </button>
                </div>
                
                {/* AI Content Notice - Important Disclaimer */}
                {!useCustomText && (
                  <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 mb-4">
                    <p className="text-amber-300 text-sm flex items-start gap-2">
                      <Sparkles className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span>
                        <strong>AI-Assisted Content:</strong> Content below is generated from information you provide. 
                        You are responsible for reviewing and approving accuracy before publication. 
                        AI-generated sections are labelled on your public profile.
                      </span>
                    </p>
                  </div>
                )}
                
                {/* Custom Text Mode */}
                {useCustomText && (
                  <div className="space-y-4">
                    <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mb-4">
                      <p className="text-blue-300 text-sm">
                        <strong>Custom Text Mode:</strong> Write your profile description exactly as you want it to appear. 
                        No AI modifications will be applied.
                      </p>
                    </div>
                    <div>
                      <label className="block text-zinc-400 text-sm font-medium mb-2">
                        Profile Description
                      </label>
                      <textarea
                        value={formData.profile_text}
                        onChange={(e) => setFormData(prev => ({ ...prev, profile_text: e.target.value }))}
                        placeholder="Write your company profile description here. This text will appear exactly as written on your public profile page..."
                        rows={12}
                        className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 resize-none"
                        data-testid="custom-profile-text"
                      />
                      <p className="text-zinc-500 text-xs mt-2">
                        {formData.profile_text?.length || 0} characters
                      </p>
                    </div>
                  </div>
                )}
                
                {/* AI Sections Mode */}
                {!useCustomText && (
                  <>
                    {/* AI Content Section */}
                    {formData.profile_sections && (
                      <div>
                        <div className="flex items-center justify-end mb-4">
                          <button
                            type="button"
                            onClick={handleRegenerateAI}
                            disabled={isRegeneratingAI}
                            className="flex items-center gap-2 text-sm text-[#C41E3A] hover:text-[#A01830] disabled:opacity-50"
                          >
                            <RefreshCw className={`w-4 h-4 ${isRegeneratingAI ? 'animate-spin' : ''}`} />
                            {isRegeneratingAI ? 'Regenerating...' : 'Regenerate All'}
                          </button>
                        </div>
                        <p className="text-zinc-500 text-sm mb-4">
                          Edit the AI-generated sections below or regenerate based on your updated inputs.
                        </p>
                        
                        <div className="space-y-4">
                          {formData.profile_sections.who_we_are && (
                            <div>
                              <label className="block text-zinc-400 text-sm font-medium mb-2">Who We Are</label>
                              <textarea
                                value={formData.profile_sections.who_we_are}
                                onChange={(e) => setFormData(prev => ({
                                  ...prev,
                                  profile_sections: { ...prev.profile_sections, who_we_are: e.target.value }
                                }))}
                                rows={3}
                                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50 focus:border-[#C41E3A] resize-none"
                              />
                            </div>
                          )}
                          
                          {formData.profile_sections.our_presence && (
                            <div>
                              <label className="block text-zinc-400 text-sm font-medium mb-2">Our Presence</label>
                              <textarea
                                value={formData.profile_sections.our_presence}
                                onChange={(e) => setFormData(prev => ({
                                  ...prev,
                                  profile_sections: { ...prev.profile_sections, our_presence: e.target.value }
                                }))}
                                rows={3}
                                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50 focus:border-[#C41E3A] resize-none"
                              />
                            </div>
                          )}
                          
                          {formData.profile_sections.what_we_seek && (
                            <div>
                              <label className="block text-zinc-400 text-sm font-medium mb-2">What We&apos;re Looking For</label>
                              <textarea
                                value={formData.profile_sections.what_we_seek}
                                onChange={(e) => setFormData(prev => ({
                                  ...prev,
                                  profile_sections: { ...prev.profile_sections, what_we_seek: e.target.value }
                                }))}
                                rows={3}
                                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50 focus:border-[#C41E3A] resize-none"
                              />
                            </div>
                          )}
                          
                          {formData.profile_sections.why_partner && (
                            <div>
                              <label className="block text-zinc-400 text-sm font-medium mb-2">Why Partner With Us</label>
                              <textarea
                                value={formData.profile_sections.why_partner}
                                onChange={(e) => setFormData(prev => ({
                                  ...prev,
                                  profile_sections: { ...prev.profile_sections, why_partner: e.target.value }
                                }))}
                                rows={3}
                                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50 focus:border-[#C41E3A] resize-none"
                              />
                            </div>
                          )}
                          
                          {formData.profile_sections.call_to_action && (
                            <div>
                              <label className="block text-zinc-400 text-sm font-medium mb-2">Call to Action</label>
                              <textarea
                                value={formData.profile_sections.call_to_action}
                                onChange={(e) => setFormData(prev => ({
                                  ...prev,
                                  profile_sections: { ...prev.profile_sections, call_to_action: e.target.value }
                                }))}
                                rows={2}
                                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50 focus:border-[#C41E3A] resize-none"
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* Generate AI Button (if no sections yet) */}
                    {!formData.profile_sections && formData.target_audience && formData.key_message && (
                      <div className="mt-4">
                        <button
                          type="button"
                          onClick={handleRegenerateAI}
                          disabled={isRegeneratingAI}
                          className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-[#C41E3A] to-[#A01830] hover:from-[#A01830] hover:to-[#8a1428] text-white py-3 rounded-lg font-semibold transition-all disabled:opacity-50"
                        >
                          {isRegeneratingAI ? (
                            <>
                              <Loader2 className="w-5 h-5 animate-spin" />
                              Generating AI Content...
                            </>
                          ) : (
                            <>
                              <Sparkles className="w-5 h-5" />
                              Generate AI Content
                            </>
                          )}
                        </button>
                        <p className="text-zinc-500 text-xs text-center mt-2">
                          Generate structured profile sections based on your inputs
                        </p>
                      </div>
                    )}
                  </>
                )}
              </div>
              
              {/* Audience Messages */}
              {formData.audience_messages && formData.audience_messages.length > 0 && (
                <div className="border-t border-zinc-700 pt-6 mt-6">
                  <h3 className="text-lg font-semibold text-white mb-3">
                    Messages for {formData.target_audience || 'Your Audience'}
                  </h3>
                  <div className="space-y-3">
                    {formData.audience_messages.filter(Boolean).map((msg, idx) => (
                      <div key={idx} className="bg-zinc-800 rounded-lg p-3">
                        <textarea
                          value={msg}
                          onChange={(e) => {
                            const updated = [...formData.audience_messages];
                            updated[idx] = e.target.value;
                            setFormData(prev => ({ ...prev, audience_messages: updated }));
                          }}
                          rows={2}
                          className="w-full bg-transparent text-zinc-300 text-sm focus:outline-none resize-none"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Step: Details */}
          {currentStepName === 'Details' && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <Package className="w-5 h-5 text-[#C41E3A]" />
                Company Details
              </h2>
              
              <div>
                <label className="block text-zinc-300 text-sm font-medium mb-2">
                  Products / Services *
                </label>
                <textarea
                  value={formData.products_services}
                  onChange={(e) => handleInputChange('products_services', e.target.value)}
                  placeholder="Describe what your company offers..."
                  rows={3}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50 focus:border-[#C41E3A] resize-none"
                />
              </div>
              
              <div>
                <label className="block text-zinc-300 text-sm font-medium mb-2">
                  <Globe className="w-4 h-4 inline mr-1" />
                  Key Markets / Regions
                </label>
                <input
                  type="text"
                  value={formData.key_markets}
                  onChange={(e) => handleInputChange('key_markets', e.target.value)}
                  placeholder="e.g., Africa, Southeast Asia, Europe"
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50 focus:border-[#C41E3A]"
                />
              </div>
              
              <div>
                <label className="block text-zinc-300 text-sm font-medium mb-2">
                  <Handshake className="w-4 h-4 inline mr-1" />
                  Looking For
                </label>
                <input
                  type="text"
                  value={formData.looking_for}
                  onChange={(e) => handleInputChange('looking_for', e.target.value)}
                  placeholder="e.g., Distribution partners, Strategic alliances"
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50 focus:border-[#C41E3A]"
                />
              </div>
              
              {/* KPI Stats */}
              <div className="border-t border-zinc-700 pt-6 mt-6">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <Globe className="w-5 h-5 text-[#C41E3A]" />
                  Company Stats (Optional)
                </h3>
                <p className="text-zinc-400 text-sm mb-4">These numbers will be displayed as highlight cards on your profile</p>
                
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-zinc-300 text-sm font-medium mb-2">
                      Products / SKUs
                    </label>
                    <input
                      type="number"
                      value={formData.products_count}
                      onChange={(e) => handleInputChange('products_count', e.target.value)}
                      placeholder="e.g., 150"
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50 focus:border-[#C41E3A]"
                    />
                  </div>
                  <div>
                    <label className="block text-zinc-300 text-sm font-medium mb-2">
                      Clients Served
                    </label>
                    <input
                      type="number"
                      value={formData.clients_count}
                      onChange={(e) => handleInputChange('clients_count', e.target.value)}
                      placeholder="e.g., 500"
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50 focus:border-[#C41E3A]"
                    />
                  </div>
                  <div>
                    <label className="block text-zinc-300 text-sm font-medium mb-2">
                      Countries / Markets
                    </label>
                    <input
                      type="text"
                      value={formData.countries_count}
                      onChange={(e) => handleInputChange('countries_count', e.target.value)}
                      placeholder="e.g., 25 or India, Thailand, Vietnam"
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50 focus:border-[#C41E3A]"
                    />
                    <p className="text-zinc-500 text-xs mt-1">Enter a number or list country names</p>
                  </div>
                </div>
              </div>
              
              <div>
                <label className="block text-zinc-300 text-sm font-medium mb-2">
                  <Award className="w-4 h-4 inline mr-1" />
                  Certifications
                </label>
                <div className="flex flex-wrap gap-2">
                  {certificationOptions.map(cert => (
                    <button
                      key={cert}
                      type="button"
                      onClick={() => toggleCertification(cert)}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                        formData.certifications.includes(cert)
                          ? 'bg-[#C41E3A] text-white'
                          : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                      }`}
                    >
                      {cert}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Contact Info */}
              <div className="border-t border-zinc-700 pt-6 mt-6">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <User className="w-5 h-5 text-[#C41E3A]" />
                  Contact Information
                </h3>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-zinc-300 text-sm font-medium mb-2">
                      Contact Name *
                    </label>
                    <input
                      type="text"
                      value={formData.contact_name}
                      onChange={(e) => handleInputChange('contact_name', e.target.value)}
                      placeholder="e.g., Rajesh Kumar"
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50 focus:border-[#C41E3A]"
                    />
                  </div>
                  <div>
                    <label className="block text-zinc-300 text-sm font-medium mb-2">
                      Designation
                    </label>
                    <input
                      type="text"
                      value={formData.contact_designation}
                      onChange={(e) => handleInputChange('contact_designation', e.target.value)}
                      placeholder="e.g., Director - International Business"
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50 focus:border-[#C41E3A]"
                    />
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="block text-zinc-300 text-sm font-medium mb-2">
                      <Mail className="w-4 h-4 inline mr-1" />
                      Email *
                    </label>
                    <input
                      type="email"
                      value={formData.contact_email}
                      onChange={(e) => handleInputChange('contact_email', e.target.value)}
                      placeholder="e.g., exports@company.com"
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50 focus:border-[#C41E3A]"
                    />
                  </div>
                  <div>
                    <label className="block text-zinc-300 text-sm font-medium mb-2">
                      <Phone className="w-4 h-4 inline mr-1" />
                      Phone
                    </label>
                    <input
                      type="tel"
                      value={formData.contact_phone}
                      onChange={(e) => handleInputChange('contact_phone', e.target.value)}
                      placeholder="e.g., +91 98765 43210"
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50 focus:border-[#C41E3A]"
                    />
                  </div>
                </div>
                
                <div className="mt-4">
                  <label className="block text-zinc-300 text-sm font-medium mb-2">
                    <LinkIcon className="w-4 h-4 inline mr-1" />
                    Website
                  </label>
                  <input
                    type="url"
                    value={formData.website}
                    onChange={(e) => handleInputChange('website', e.target.value)}
                    placeholder="e.g., https://www.company.com"
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50 focus:border-[#C41E3A]"
                  />
                </div>
              </div>
              
              {/* Key Contact Profile - Optional Section */}
              <div className="border-t border-zinc-700 pt-6 mt-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-bold text-white flex items-center gap-2">
                      <User className="w-5 h-5 text-[#C41E3A]" />
                      Key Contact Profile
                    </h3>
                    <p className="text-zinc-400 text-sm">Add a detailed profile for your key contact person</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.include_key_contact}
                      onChange={(e) => handleInputChange('include_key_contact', e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-zinc-700 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-[#C41E3A]/50 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#C41E3A]"></div>
                    <span className="ml-2 text-sm text-zinc-400">{formData.include_key_contact ? 'Enabled' : 'Optional'}</span>
                  </label>
                </div>
                
                {formData.include_key_contact && (
                  <div className="space-y-4 bg-zinc-800/30 border border-zinc-700 rounded-lg p-4">
                    {/* Contact Photo */}
                    <div>
                      <label className="block text-zinc-300 text-sm font-medium mb-2">
                        Contact Photo
                      </label>
                      <div className="flex items-center gap-4">
                        {formData.key_contact.photo_url ? (
                          <div className="relative">
                            <img 
                              src={formData.key_contact.photo_url} 
                              alt="Contact" 
                              className="w-20 h-20 object-cover rounded-full border-2 border-zinc-600"
                            />
                            <button
                              type="button"
                              onClick={removeContactPhoto}
                              className="absolute -top-1 -right-1 bg-red-500 hover:bg-red-600 text-white rounded-full p-1"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ) : (
                          <div className="w-20 h-20 bg-zinc-700 rounded-full flex items-center justify-center">
                            <User className="w-8 h-8 text-zinc-500" />
                          </div>
                        )}
                        <div>
                          <input
                            ref={contactPhotoInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleContactPhotoUpload}
                            className="hidden"
                            id="contact-photo-upload"
                          />
                          <label
                            htmlFor="contact-photo-upload"
                            className={`inline-flex items-center gap-2 bg-zinc-700 hover:bg-zinc-600 text-white px-4 py-2 rounded-lg text-sm cursor-pointer transition-colors ${isUploadingContactPhoto ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            {isUploadingContactPhoto ? (
                              <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Uploading...
                              </>
                            ) : (
                              <>
                                <Upload className="w-4 h-4" />
                                Upload Photo
                              </>
                            )}
                          </label>
                          <p className="text-zinc-500 text-xs mt-1">Professional headshot recommended</p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Experience & Expertise */}
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-zinc-300 text-sm font-medium mb-2">
                          Years of Experience
                        </label>
                        <input
                          type="text"
                          value={formData.key_contact.years_experience}
                          onChange={(e) => updateKeyContact('years_experience', e.target.value)}
                          placeholder="e.g., 25+"
                          className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50"
                        />
                      </div>
                      <div>
                        <label className="block text-zinc-300 text-sm font-medium mb-2">
                          LinkedIn Profile
                        </label>
                        <input
                          type="url"
                          value={formData.key_contact.linkedin_url}
                          onChange={(e) => updateKeyContact('linkedin_url', e.target.value)}
                          placeholder="https://linkedin.com/in/..."
                          className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50"
                        />
                      </div>
                    </div>
                    
                    {/* Areas of Expertise */}
                    <div>
                      <label className="block text-zinc-300 text-sm font-medium mb-2">
                        Areas of Expertise
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {expertiseOptions.map(expertise => (
                          <button
                            key={expertise}
                            type="button"
                            onClick={() => toggleExpertise(expertise)}
                            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                              formData.key_contact.expertise_areas.includes(expertise)
                                ? 'bg-[#C41E3A] text-white'
                                : 'bg-zinc-700 text-zinc-400 hover:bg-zinc-600'
                            }`}
                          >
                            {expertise}
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    {/* Notable Achievements */}
                    <div>
                      <label className="block text-zinc-300 text-sm font-medium mb-2">
                        Notable Achievements / Background
                      </label>
                      <textarea
                        value={formData.key_contact.achievements}
                        onChange={(e) => updateKeyContact('achievements', e.target.value)}
                        placeholder="e.g., Expanded operations to 15+ countries, Led $50M export growth, Key speaker at international trade forums..."
                        rows={2}
                        className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50 resize-none"
                      />
                    </div>
                    
                    {/* AI-Generated Bio */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-zinc-300 text-sm font-medium">
                          Professional Introduction
                        </label>
                        <button
                          type="button"
                          onClick={generateContactBio}
                          disabled={isGeneratingContactBio || !formData.contact_name}
                          className="text-[#C41E3A] text-sm font-medium hover:underline flex items-center gap-1 disabled:opacity-50"
                        >
                          {isGeneratingContactBio ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" />
                              Generating...
                            </>
                          ) : (
                            <>
                              <Sparkles className="w-4 h-4" />
                              Generate with AI
                            </>
                          )}
                        </button>
                      </div>
                      <textarea
                        value={formData.key_contact.bio_text}
                        onChange={(e) => updateKeyContact('bio_text', e.target.value)}
                        placeholder="A professional introduction paragraph will be generated based on the information above, or you can write your own..."
                        rows={3}
                        className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50 resize-none"
                      />
                    </div>
                    
                    {/* Contact's Videos */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-zinc-300 text-sm font-medium">
                          Contact&apos;s Videos (Interviews, Talks)
                        </label>
                        {formData.key_contact.videos.length < 3 && (
                          <button
                            type="button"
                            onClick={addKeyContactVideo}
                            className="text-[#C41E3A] text-sm font-medium hover:underline"
                          >
                            + Add Video
                          </button>
                        )}
                      </div>
                      {formData.key_contact.videos.length === 0 ? (
                        <p className="text-zinc-500 text-sm">No videos added. These could be interviews, panel discussions, or presentations.</p>
                      ) : (
                        <div className="space-y-2">
                          {formData.key_contact.videos.map((video, index) => (
                            <div key={index} className="flex gap-2">
                              <input
                                type="url"
                                value={video.url}
                                onChange={(e) => updateKeyContactVideo(index, 'url', e.target.value)}
                                placeholder="YouTube URL"
                                className="flex-1 bg-zinc-800 border border-zinc-700 rounded px-3 py-2 text-white text-sm placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50"
                              />
                              <input
                                type="text"
                                value={video.title}
                                onChange={(e) => updateKeyContactVideo(index, 'title', e.target.value)}
                                placeholder="Video Title"
                                className="w-40 bg-zinc-800 border border-zinc-700 rounded px-3 py-2 text-white text-sm placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50"
                              />
                              <button
                                type="button"
                                onClick={() => removeKeyContactVideo(index)}
                                className="text-red-400 hover:text-red-300 px-2"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Resources / Documents Link - Premium Feature (Requires Active Subscription or Admin) */}
              <div className="border-t border-zinc-700 pt-6 mt-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-bold text-white flex items-center gap-2">
                      <FolderOpen className="w-5 h-5 text-[#C41E3A]" />
                      Company Resources
                      {subscriptionStatus !== 'active' && !isAdmin && (
                        <span className="text-xs bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded-full font-normal">
                          Premium
                        </span>
                      )}
                      {isAdmin && subscriptionStatus !== 'active' && (
                        <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full font-normal">
                          Admin Override
                        </span>
                      )}
                    </h3>
                    <p className="text-zinc-400 text-sm">Add a Google Drive link to share brochures, presentations, etc.</p>
                  </div>
                  {(subscriptionStatus === 'active' || isAdmin) ? (
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.resources_enabled}
                        onChange={(e) => {
                          handleInputChange('resources_enabled', e.target.checked);
                          if (!e.target.checked) {
                            setResourcesLinkValid(null);
                          }
                        }}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-zinc-700 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-[#C41E3A]/50 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#C41E3A]"></div>
                      <span className="ml-2 text-sm text-zinc-400">{formData.resources_enabled ? 'Enabled' : 'Optional'}</span>
                    </label>
                  ) : (
                    <span className="text-xs text-zinc-500 bg-zinc-800 px-3 py-1.5 rounded-lg">
                      Requires Subscription
                    </span>
                  )}
                </div>
                
                {/* Show subscription prompt if not active and not admin */}
                {subscriptionStatus !== 'active' && !isAdmin && (
                  <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                    <p className="text-amber-300 text-sm mb-2">
                      <strong>Premium Feature:</strong> Share downloadable resources with profile visitors.
                    </p>
                    <p className="text-zinc-400 text-sm">
                      Subscribe to a 6-month or 1-year plan to unlock this feature along with profile visibility.
                    </p>
                  </div>
                )}
                
                {(subscriptionStatus === 'active' || isAdmin) && formData.resources_enabled && (
                  <div className="space-y-4 bg-zinc-800/30 border border-zinc-700 rounded-lg p-4">
                    <div>
                      <label className="block text-zinc-300 text-sm font-medium mb-2">
                        Button Label
                      </label>
                      <input
                        type="text"
                        value={formData.resources_label}
                        onChange={(e) => handleInputChange('resources_label', e.target.value)}
                        placeholder="e.g., Company Brochure, Product Catalog"
                        className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50 focus:border-[#C41E3A]"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-zinc-300 text-sm font-medium mb-2">
                        Google Drive / Docs Link *
                      </label>
                      <div className="relative">
                        <input
                          type="url"
                          value={formData.resources_link}
                          onChange={(e) => {
                            handleInputChange('resources_link', e.target.value);
                            setResourcesLinkValid(null); // Reset validation on change
                          }}
                          onBlur={() => {
                            if (formData.resources_link) {
                              validateResourcesLink(formData.resources_link);
                            }
                          }}
                          placeholder="https://drive.google.com/drive/folders/..."
                          className={`w-full bg-zinc-800 border rounded-lg px-4 py-3 pr-12 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50 ${
                            resourcesLinkValid === false 
                              ? 'border-red-500' 
                              : resourcesLinkValid === true 
                              ? 'border-green-500' 
                              : 'border-zinc-700'
                          }`}
                          data-testid="resources-link-input"
                        />
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          {resourcesLinkChecking && (
                            <Loader2 className="w-5 h-5 text-zinc-400 animate-spin" />
                          )}
                          {!resourcesLinkChecking && resourcesLinkValid === true && (
                            <CheckCircle className="w-5 h-5 text-green-500" />
                          )}
                          {!resourcesLinkChecking && resourcesLinkValid === false && (
                            <XCircle className="w-5 h-5 text-red-500" />
                          )}
                        </div>
                      </div>
                      
                      {resourcesLinkValid === false && (
                        <p className="text-red-400 text-sm mt-2">
                          This link appears to be invalid or not publicly accessible. Please ensure:
                          <br />• The link is from Google Drive or Google Docs
                          <br />• The sharing is set to &quot;Anyone with the link&quot;
                        </p>
                      )}
                      
                      {resourcesLinkValid === true && (
                        <p className="text-green-400 text-sm mt-2 flex items-center gap-1">
                          <CheckCircle className="w-4 h-4" />
                          Link validated successfully
                        </p>
                      )}
                      
                      <p className="text-zinc-500 text-xs mt-2">
                        Supported: Google Drive folders, Google Docs, Sheets, Slides, or Forms
                      </p>
                    </div>
                    
                    {/* Validate Button */}
                    <button
                      type="button"
                      onClick={() => validateResourcesLink(formData.resources_link)}
                      disabled={!formData.resources_link || resourcesLinkChecking}
                      className="flex items-center gap-2 text-sm text-[#C41E3A] hover:text-[#A01830] disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {resourcesLinkChecking ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Validating...
                        </>
                      ) : (
                        <>
                          <Check className="w-4 h-4" />
                          Validate Link
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
              
              {/* Multi-Language Translation Section */}
              <div className="border-t border-zinc-700 pt-6 mt-6" id="multi-language-section">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-bold text-white flex items-center gap-2">
                      <Globe className="w-5 h-5 text-[#C41E3A]" />
                      Multi-Language Translation
                      <span className="text-xs bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded-full font-normal">
                        Pro add-on
                      </span>
                    </h3>
                    <p className="text-zinc-400 text-sm">Allow visitors to view your profile in different languages</p>
                  </div>
                  {features && !features.translation && !formData.multi_language_enabled ? (
                    <button
                      type="button"
                      onClick={() => navigate('/pricing')}
                      className="bg-[#C41E3A] hover:bg-[#A01830] text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                      data-testid="translation-unlock-btn"
                    >
                      Unlock
                    </button>
                  ) : (
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.multi_language_enabled}
                      onChange={(e) => handleInputChange('multi_language_enabled', e.target.checked)}
                      className="sr-only peer"
                      data-testid="multi-language-toggle"
                    />
                    <div className="w-11 h-6 bg-zinc-700 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-[#C41E3A]/50 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#C41E3A]"></div>
                    <span className="ml-2 text-sm text-zinc-400">{formData.multi_language_enabled ? 'Enabled' : 'Disabled'}</span>
                  </label>
                  )}
                </div>
                
                {formData.multi_language_enabled && (
                  <div className="bg-zinc-800/30 border border-zinc-700 rounded-lg p-4">
                    <p className="text-zinc-300 text-sm mb-3">
                      When enabled, visitors will see a language selector (🌐) in the header with these options:
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {[
                        { flag: '🇬🇧', name: 'English' },
                        { flag: '🇻🇳', name: 'Vietnamese' },
                        { flag: '🇹🇭', name: 'Thai' },
                        { flag: '🇨🇳', name: 'Chinese' },
                        { flag: '🇯🇵', name: 'Japanese' },
                        { flag: '🇮🇳', name: 'Hindi' },
                        { flag: '🇦🇪', name: 'Arabic' },
                        { flag: '🇮🇩', name: 'Indonesian' },
                        { flag: '🇲🇾', name: 'Malay' },
                        { flag: '🇰🇷', name: 'Korean' },
                        { flag: '🇩🇪', name: 'German' },
                        { flag: '🇫🇷', name: 'French' },
                        { flag: '🇪🇸', name: 'Spanish' },
                      ].map((lang) => (
                        <span key={lang.name} className="inline-flex items-center gap-1.5 bg-zinc-700/50 text-zinc-300 px-3 py-1.5 rounded-lg text-sm">
                          <span>{lang.flag}</span>
                          <span>{lang.name}</span>
                        </span>
                      ))}
                    </div>
                    <p className="text-zinc-500 text-xs mt-3">
                      Translations are generated automatically using AI. Original content remains unchanged.
                    </p>
                  </div>
                )}
              </div>
              
              {/* Social Media URLs Section */}
              <div className="border-t border-zinc-700 pt-6 mt-6" id="social-urls-section">
                <h3 className="text-lg font-bold text-white flex items-center gap-2 mb-4">
                  <LinkIcon className="w-5 h-5 text-[#C41E3A]" />
                  Social Media Profiles
                </h3>
                <p className="text-zinc-400 text-sm mb-4">
                  Link your social media profiles to improve your profile score and help visitors find you.
                </p>
                
                <div className="grid md:grid-cols-2 gap-4">
                  {/* LinkedIn */}
                  <div>
                    <label className="block text-zinc-400 text-sm font-medium mb-2">
                      LinkedIn Company Page
                    </label>
                    <input
                      type="url"
                      value={formData.social_urls.linkedin}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        social_urls: { ...prev.social_urls, linkedin: e.target.value }
                      }))}
                      placeholder="https://linkedin.com/company/..."
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50 focus:border-[#C41E3A]"
                      data-testid="social-linkedin-input"
                    />
                  </div>
                  
                  {/* YouTube */}
                  <div>
                    <label className="block text-zinc-400 text-sm font-medium mb-2">
                      YouTube Channel
                    </label>
                    <input
                      type="url"
                      value={formData.social_urls.youtube}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        social_urls: { ...prev.social_urls, youtube: e.target.value }
                      }))}
                      placeholder="https://youtube.com/@..."
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50 focus:border-[#C41E3A]"
                      data-testid="social-youtube-input"
                    />
                  </div>
                  
                  {/* X (Twitter) */}
                  <div>
                    <label className="block text-zinc-400 text-sm font-medium mb-2">
                      X (Twitter)
                    </label>
                    <input
                      type="url"
                      value={formData.social_urls.twitter}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        social_urls: { ...prev.social_urls, twitter: e.target.value }
                      }))}
                      placeholder="https://x.com/..."
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50 focus:border-[#C41E3A]"
                      data-testid="social-twitter-input"
                    />
                  </div>
                  
                  {/* Instagram */}
                  <div>
                    <label className="block text-zinc-400 text-sm font-medium mb-2">
                      Instagram
                    </label>
                    <input
                      type="url"
                      value={formData.social_urls.instagram}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        social_urls: { ...prev.social_urls, instagram: e.target.value }
                      }))}
                      placeholder="https://instagram.com/..."
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50 focus:border-[#C41E3A]"
                      data-testid="social-instagram-input"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Step: Media */}
          {currentStepName === 'Media' && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <Video className="w-5 h-5 text-[#C41E3A]" />
                Media & Videos
              </h2>
              
              <p className="text-zinc-400 text-sm">
                Add videos to make your profile stand out. YouTube links are recommended for easy embedding.
              </p>
              
              {/* Logo Upload/URL */}
              <div>
                <label className="block text-zinc-300 text-sm font-medium mb-2">
                  <Image className="w-4 h-4 inline mr-1" />
                  Company Logo
                </label>
                
                {/* Logo Preview */}
                {formData.logo_url && (
                  <div className="mb-3 relative inline-block">
                    <img 
                      src={formData.logo_url} 
                      alt="Logo preview" 
                      className="h-20 w-auto object-contain bg-white rounded-lg p-2"
                    />
                    <button
                      type="button"
                      onClick={removeLogo}
                      className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                )}
                
                {/* Upload Options */}
                <div className="flex flex-col sm:flex-row gap-3">
                  {/* File Upload */}
                  <div className="flex-1">
                    <input
                      ref={logoInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      className="hidden"
                      id="logo-upload"
                    />
                    <label
                      htmlFor="logo-upload"
                      className={`flex items-center justify-center gap-2 w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-zinc-300 cursor-pointer hover:bg-zinc-700 transition-colors ${isUploadingLogo ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {isUploadingLogo ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4" />
                          Upload Logo
                        </>
                      )}
                    </label>
                  </div>
                  
                  <div className="text-zinc-500 text-sm self-center">or</div>
                  
                  {/* URL Input */}
                  <div className="flex-1">
                    <input
                      type="url"
                      value={formData.logo_url}
                      onChange={(e) => handleInputChange('logo_url', e.target.value)}
                      placeholder="Paste logo URL"
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50 focus:border-[#C41E3A]"
                    />
                  </div>
                </div>
              </div>
              
              {/* Hero Video */}
              <div className="border border-zinc-700 rounded-lg p-4">
                <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                  <Video className="w-4 h-4 text-[#C41E3A]" />
                  Hero Video (Featured)
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-zinc-400 text-xs mb-1">Video URL</label>
                    <input
                      type="url"
                      value={formData.hero_video?.url || ''}
                      onChange={(e) => handleInputChange('hero_video', {
                        ...formData.hero_video,
                        type: 'youtube',
                        url: e.target.value
                      })}
                      placeholder="https://youtube.com/watch?v=..."
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50"
                    />
                  </div>
                  <div>
                    <label className="block text-zinc-400 text-xs mb-1">Video Title</label>
                    <input
                      type="text"
                      value={formData.hero_video?.title || ''}
                      onChange={(e) => handleInputChange('hero_video', {
                        ...formData.hero_video,
                        title: e.target.value
                      })}
                      placeholder="e.g., Company Overview"
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50"
                    />
                  </div>
                </div>
              </div>
              
              {/* Additional Videos */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-white font-semibold flex items-center gap-2">
                    <Youtube className="w-4 h-4 text-[#C41E3A]" />
                    Additional Videos
                  </h3>
                  {formData.additional_videos.length < 5 && (
                    <button
                      type="button"
                      onClick={addVideoSlot}
                      className="text-[#C41E3A] text-sm font-medium hover:underline"
                    >
                      + Add Video
                    </button>
                  )}
                </div>
                
                {formData.additional_videos.length === 0 ? (
                  <div className="border-2 border-dashed border-zinc-700 rounded-lg p-6 text-center">
                    <Youtube className="w-8 h-8 text-zinc-600 mx-auto mb-2" />
                    <p className="text-zinc-500 text-sm">No additional videos added</p>
                    <button
                      type="button"
                      onClick={addVideoSlot}
                      className="text-[#C41E3A] text-sm font-medium mt-2 hover:underline"
                    >
                      + Add your first video
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {formData.additional_videos.map((video, index) => (
                      <div key={index} className="border border-zinc-700 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-zinc-400 text-xs">Video {index + 1}</span>
                          <button
                            type="button"
                            onClick={() => removeVideo(index)}
                            className="text-red-400 text-xs hover:underline"
                          >
                            Remove
                          </button>
                        </div>
                        <div className="grid md:grid-cols-2 gap-3">
                          <input
                            type="url"
                            value={video.url}
                            onChange={(e) => updateVideo(index, 'url', e.target.value)}
                            placeholder="YouTube URL"
                            className="w-full bg-zinc-800 border border-zinc-700 rounded px-3 py-2 text-white text-sm placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50"
                          />
                          <input
                            type="text"
                            value={video.title}
                            onChange={(e) => updateVideo(index, 'title', e.target.value)}
                            placeholder="Video Title"
                            className="w-full bg-zinc-800 border border-zinc-700 rounded px-3 py-2 text-white text-sm placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* BrandStage Showcase Section */}
              <div className="border-t border-zinc-700 pt-6">
                {features && !features.showcase ? (
                  <FeatureLock title="Carousel + Showcase" testId="showcase" />
                ) : (
                  <ShowcaseManager 
                    profileId={profileId} 
                    profileSlug={profileData?.slug}
                    isAdmin={isAdmin}
                  />
                )}
              </div>
              
              {/* Opportunity Showcase Pages */}
              <div className="border-t border-zinc-700 pt-6">
                <OpportunityManager
                  profileId={profileId}
                  profileSlug={profileData?.slug}
                  isAdmin={isAdmin}
                />
              </div>
              
              {/* Translation Editor Section - Admin Only */}
              {isAdmin && (
                <div className="border-t border-zinc-700 pt-6">
                  <TranslationEditor profileId={profileId} />
                </div>
              )}
              
              {/* Testimonials Section */}
              <div className="border-t border-zinc-700 pt-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-white font-semibold flex items-center gap-2">
                    <MessageSquare className="w-4 h-4 text-[#C41E3A]" />
                    Testimonials
                    <span className="text-zinc-500 text-xs font-normal">(Optional)</span>
                  </h3>
                  <button
                    type="button"
                    onClick={() => (features && !features.testimonials ? navigate('/pricing') : setShowTestimonialModal(true))}
                    className="bg-[#C41E3A] hover:bg-[#A01830] text-white px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1.5 transition-colors"
                    data-testid={features && !features.testimonials ? 'testimonials-unlock-btn' : 'request-testimonial-btn'}
                  >
                    <Send className="w-3.5 h-3.5" />
                    {features && !features.testimonials ? 'Unlock Testimonials' : 'Request Testimonial'}
                  </button>
                </div>
                
                <p className="text-zinc-400 text-sm mb-4">
                  Collect video testimonials from clients and partners. They&apos;ll receive an email with a link to record their video.
                </p>
                
                {testimonials.length === 0 ? (
                  <div className="border-2 border-dashed border-zinc-700 rounded-lg p-6 text-center">
                    <MessageSquare className="w-8 h-8 text-zinc-600 mx-auto mb-2" />
                    <p className="text-zinc-500 text-sm">No testimonials yet</p>
                    <p className="text-zinc-600 text-xs mt-1">
                      Request testimonials from satisfied clients to build trust
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {testimonials.map((testimonial) => (
                      <div 
                        key={testimonial.id} 
                        className="border border-zinc-700 rounded-lg p-4 bg-zinc-800/30"
                        data-testid="testimonial-item"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex items-start gap-3 flex-1">
                            {/* Thumbnail or placeholder */}
                            <div className="w-16 h-12 bg-zinc-700 rounded-lg overflow-hidden flex-shrink-0 flex items-center justify-center">
                              {testimonial.video_url ? (
                                <Play className="w-5 h-5 text-zinc-400" />
                              ) : (
                                <Clock className="w-5 h-5 text-zinc-500" />
                              )}
                            </div>
                            
                            <div className="flex-1 min-w-0">
                              <p className="text-white font-medium">{testimonial.contact_name}</p>
                              <p className="text-zinc-400 text-sm">{testimonial.contact_email}</p>
                              
                              {/* Status Badge */}
                              <div className="flex items-center gap-2 mt-2">
                                <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                  testimonial.status === 'approved' 
                                    ? 'bg-green-500/20 text-green-400' 
                                    : testimonial.status === 'recorded' || testimonial.status === 'pending_review'
                                    ? 'bg-amber-500/20 text-amber-400'
                                    : testimonial.status === 'rejected'
                                    ? 'bg-red-500/20 text-red-400'
                                    : 'bg-zinc-600/30 text-zinc-400'
                                }`}>
                                  {testimonial.status === 'pending_recording' ? 'Pending Recording' :
                                   testimonial.status === 'recorded' ? 'Awaiting Approval' :
                                   testimonial.status === 'pending_review' ? 'Awaiting Approval' :
                                   testimonial.status === 'approved' ? 'Approved' :
                                   testimonial.status === 'rejected' ? 'Rejected' : 'Pending'}
                                </span>
                                
                                {testimonial.video_url && (
                                  <a 
                                    href={testimonial.video_url} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="text-[#C41E3A] text-xs hover:underline flex items-center gap-1"
                                  >
                                    <Play className="w-3 h-3" /> Preview
                                  </a>
                                )}
                              </div>
                            </div>
                          </div>
                          
                          {/* Visibility Toggle - Only for approved testimonials */}
                          {testimonial.status === 'approved' && (
                            <div className="flex flex-col items-end gap-1">
                              <span className="text-zinc-500 text-xs">Visible on Profile</span>
                              <button
                                type="button"
                                onClick={() => toggleTestimonialVisibility(testimonial.id)}
                                disabled={togglingVisibility === testimonial.id}
                                className={`relative w-12 h-6 rounded-full transition-colors ${
                                  testimonial.is_visible ? 'bg-green-600' : 'bg-zinc-600'
                                } ${togglingVisibility === testimonial.id ? 'opacity-50' : ''}`}
                                data-testid={`visibility-toggle-${testimonial.id}`}
                              >
                                <span 
                                  className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                                    testimonial.is_visible ? 'translate-x-7' : 'translate-x-1'
                                  }`}
                                />
                              </button>
                              <span className={`text-xs ${testimonial.is_visible ? 'text-green-400' : 'text-zinc-500'}`}>
                                {testimonial.is_visible ? 'Showing' : 'Hidden'}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                <p className="text-zinc-500 text-xs mt-3">
                  {testimonials.length}/5 testimonials • You can skip this and add testimonials later
                </p>
              </div>
            </div>
          )}
          
          {/* Step: Preview */}
          {currentStepName === 'Preview' && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <Eye className="w-5 h-5 text-[#C41E3A]" />
                Preview & Save
              </h2>
              
              {/* AI Generated Text */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-zinc-300 text-sm font-medium">
                    Profile Description
                  </label>
                  <button
                    type="button"
                    onClick={generateProfileText}
                    disabled={isGenerating}
                    className="text-[#C41E3A] text-sm font-medium hover:underline flex items-center gap-1"
                  >
                    {isGenerating ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4" />
                        Regenerate with AI
                      </>
                    )}
                  </button>
                </div>
                <textarea
                  value={formData.profile_text}
                  onChange={(e) => handleInputChange('profile_text', e.target.value)}
                  rows={6}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50 focus:border-[#C41E3A] resize-none"
                  placeholder="Your company profile text will appear here..."
                />
                <p className="text-zinc-500 text-xs mt-1">
                  You can edit this text before saving
                </p>
              </div>
              
              {/* Preview Card */}
              <div className="border border-zinc-700 rounded-lg p-6 bg-zinc-800/50">
                <h3 className="text-lg font-bold text-white mb-4">Preview</h3>
                
                <div className="flex items-start gap-4 mb-4">
                  {formData.logo_url ? (
                    <img src={formData.logo_url} alt="Logo" className="w-16 h-16 object-contain rounded bg-white p-1" />
                  ) : (
                    <div className="w-16 h-16 bg-zinc-700 rounded flex items-center justify-center">
                      <Building2 className="w-8 h-8 text-zinc-500" />
                    </div>
                  )}
                  <div>
                    <h4 className="text-xl font-bold text-white">{formData.company_name || 'Company Name'}</h4>
                    {formData.tagline && <p className="text-zinc-400">{formData.tagline}</p>}
                    <p className="text-zinc-500 text-sm">
                      {[formData.headquarters, formData.year_established && `Since ${formData.year_established}`].filter(Boolean).join(' • ')}
                    </p>
                  </div>
                </div>
                
                {formData.certifications.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {formData.certifications.map(cert => (
                      <span key={cert} className="bg-[#1E3A5F]/30 text-[#6B9FD4] text-xs px-2 py-1 rounded">
                        {cert}
                      </span>
                    ))}
                  </div>
                )}
                
                <p className="text-zinc-300 text-sm leading-relaxed">
                  {formData.profile_text || 'Profile description will appear here...'}
                </p>
                
                {formData.looking_for && (
                  <div className="mt-4 pt-4 border-t border-zinc-700">
                    <p className="text-zinc-400 text-sm">
                      <span className="font-semibold text-white">Looking for:</span> {formData.looking_for}
                    </p>
                  </div>
                )}
              </div>
              
              {/* Attribution notice for admin */}
              {isAdmin && formData.created_on_behalf_of && (
                <div className="bg-[#1E3A5F]/20 border border-[#1E3A5F]/30 rounded-lg p-4">
                  <p className="text-[#6B9FD4] text-sm">
                    <Handshake className="w-4 h-4 inline mr-1" />
                    Profile will be attributed to: <strong>{formData.created_on_behalf_of}</strong>
                  </p>
                </div>
              )}
              
              {/* Coupon Redemption Section - Show for inactive, active expiring soon, OR admin */}
              {(isAdmin || subscriptionStatus !== 'active' || (daysUntilExpiry !== null && daysUntilExpiry <= 30)) && (
                <div className="border border-zinc-700 bg-zinc-900/50 rounded-xl overflow-hidden" data-testid="coupon-redemption-section">
                  {/* Header */}
                  <div className="bg-gradient-to-r from-[#C41E3A]/20 to-zinc-900 px-6 py-4 border-b border-zinc-800">
                    <h3 className="text-xl font-bold text-white flex items-center gap-2">
                      {subscriptionStatus === 'active' ? (isAdmin ? 'Admin: Re-apply Coupon' : 'Renew Your Profile') : 'Activate Your Profile'}
                    </h3>
                    <p className="text-zinc-400 text-sm mt-1">
                      {subscriptionStatus === 'active' 
                        ? (isAdmin ? 'Admin override — apply a coupon to extend or reset this subscription.' : `Your profile expires in ${daysUntilExpiry} days. Renew to keep it live.`)
                        : 'Choose a plan to make your profile visible to the public.'}
                    </p>
                  </div>
                  
                  <div className="p-6">
                    {/* Pricing Plans */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                      {/* 6 Month Plan */}
                      <div 
                        className={`relative border-2 rounded-xl p-5 cursor-pointer transition-all ${
                          selectedPlan === '6_months' 
                            ? 'border-[#C41E3A] bg-[#C41E3A]/10' 
                            : 'border-zinc-700 hover:border-zinc-500'
                        }`}
                        onClick={() => setSelectedPlan('6_months')}
                        data-testid="plan-6-months"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h4 className="text-white font-semibold">6 Months</h4>
                            <p className="text-zinc-500 text-xs">Standard Plan</p>
                          </div>
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                            selectedPlan === '6_months' ? 'border-[#C41E3A] bg-[#C41E3A]' : 'border-zinc-600'
                          }`}>
                            {selectedPlan === '6_months' && <Check className="w-3 h-3 text-white" />}
                          </div>
                        </div>
                        <div className="flex items-baseline gap-1">
                          <span className="text-3xl font-bold text-white">₹4,999</span>
                          <span className="text-zinc-500 text-sm">/6 months</span>
                        </div>
                        <p className="text-zinc-500 text-xs mt-2">₹833/month</p>
                      </div>
                      
                      {/* 1 Year Plan */}
                      <div 
                        className={`relative border-2 rounded-xl p-5 cursor-pointer transition-all ${
                          selectedPlan === '1_year' 
                            ? 'border-[#C41E3A] bg-[#C41E3A]/10' 
                            : 'border-zinc-700 hover:border-zinc-500'
                        }`}
                        onClick={() => setSelectedPlan('1_year')}
                        data-testid="plan-1-year"
                      >
                        {/* Best Value Badge */}
                        <div className="absolute -top-3 left-4 bg-emerald-600 text-white text-xs font-bold px-3 py-1 rounded-full">
                          BEST VALUE
                        </div>
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h4 className="text-white font-semibold">1 Year</h4>
                            <p className="text-zinc-500 text-xs">Save ₹1,999</p>
                          </div>
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                            selectedPlan === '1_year' ? 'border-[#C41E3A] bg-[#C41E3A]' : 'border-zinc-600'
                          }`}>
                            {selectedPlan === '1_year' && <Check className="w-3 h-3 text-white" />}
                          </div>
                        </div>
                        <div className="flex items-baseline gap-1">
                          <span className="text-3xl font-bold text-white">₹7,999</span>
                          <span className="text-zinc-500 text-sm">/year</span>
                        </div>
                        <p className="text-zinc-500 text-xs mt-2">₹667/month</p>
                      </div>
                    </div>
                    
                    {/* Subscribe Button - WhatsApp or Request Callback */}
                    <div className="space-y-3">
                      <a
                        href={`https://wa.me/919003035900?text=${encodeURIComponent(`Hi, I want to activate my BrandStage profile.\n\nProfile: ${formData.company_name || 'My Company'}\nPlan: ${selectedPlan === '6_months' ? '6 Months - ₹4,999' : '1 Year - ₹7,999'}\nEmail: ${formData.contact_email || ''}`)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full bg-[#25D366] hover:bg-[#20bd5a] text-white py-4 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2 text-lg"
                        data-testid="whatsapp-subscribe-btn"
                      >
                        <MessageSquare className="w-5 h-5" />
                        Subscribe via WhatsApp - {selectedPlan === '6_months' ? '₹4,999' : '₹7,999'}
                      </a>
                      
                      <button
                        type="button"
                        onClick={() => setShowCallbackForm(!showCallbackForm)}
                        className="w-full bg-zinc-800 hover:bg-zinc-700 text-white py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 border border-zinc-700"
                        data-testid="callback-btn"
                      >
                        <Phone className="w-4 h-4" />
                        Request a Callback
                      </button>
                      
                      {showCallbackForm && (
                        <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-4 mt-3">
                          <p className="text-zinc-300 text-sm mb-3">Enter your phone number and we&apos;ll call you to complete the subscription.</p>
                          <div className="flex gap-2">
                            <input
                              type="tel"
                              value={callbackPhone}
                              onChange={(e) => setCallbackPhone(e.target.value)}
                              placeholder="+91 98765 43210"
                              className="flex-1 bg-zinc-900 border border-zinc-700 rounded-lg px-4 py-2 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50"
                            />
                            <button
                              type="button"
                              onClick={async () => {
                                if (!callbackPhone.trim()) {
                                  alert('Please enter your phone number');
                                  return;
                                }
                                alert(`Thank you! We'll call you at ${callbackPhone} shortly to complete your subscription.`);
                                setShowCallbackForm(false);
                                setCallbackPhone('');
                              }}
                              className="bg-[#C41E3A] hover:bg-[#a01830] text-white px-4 py-2 rounded-lg font-medium transition-colors"
                            >
                              Submit
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <p className="text-zinc-500 text-xs text-center mt-3">
                      Secure payment. Your profile goes live immediately after confirmation.
                    </p>
                    
                    {/* Terms Agreement */}
                    <div className="mt-4 p-4 bg-zinc-800/30 border border-zinc-700 rounded-lg">
                      <label className="flex items-start gap-3 cursor-pointer group">
                        <input
                          type="checkbox"
                          checked={termsAccepted}
                          onChange={(e) => setTermsAccepted(e.target.checked)}
                          className="sr-only"
                        />
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors ${
                          termsAccepted ? 'bg-[#C41E3A] border-[#C41E3A]' : 'border-zinc-600 group-hover:border-zinc-400'
                        }`}>
                          {termsAccepted && <Check className="w-3 h-3 text-white" />}
                        </div>
                        <span className="text-zinc-400 text-sm leading-relaxed">
                          I agree that my profile will be publicly visible once activated. I have read and accept the{' '}
                          <a href="/terms" target="_blank" className="text-[#C41E3A] hover:underline">Terms of Service</a>
                          {' '}and{' '}
                          <a href="/privacy" target="_blank" className="text-[#C41E3A] hover:underline">Privacy Policy</a>.
                          I understand that BrandStage reserves the right to review and remove content that violates community guidelines.
                        </span>
                      </label>
                    </div>
                    
                    {/* Promo Code Section - Collapsible */}
                    <div className="mt-6 pt-6 border-t border-zinc-800">
                      <button
                        type="button"
                        onClick={() => setShowPromoInput(!showPromoInput)}
                        className="text-[#C41E3A] text-sm font-medium hover:underline flex items-center gap-1"
                      >
                        <Gift className="w-4 h-4" />
                        {showPromoInput ? 'Hide promo code' : 'Have a promo code?'}
                      </button>
                      
                      {showPromoInput && (
                        <div className="mt-4">
                          <div className="flex gap-3">
                            <input
                              type="text"
                              value={couponCode}
                              onChange={(e) => {
                                setCouponCode(e.target.value.toUpperCase());
                                setCouponValidation(null);
                              }}
                              placeholder="Enter promo code"
                              className="flex-1 bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 uppercase focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50"
                              data-testid="coupon-code-input"
                            />
                            <button
                              type="button"
                              onClick={validateCoupon}
                              disabled={!couponCode.trim() || isValidatingCoupon}
                              className="bg-zinc-700 hover:bg-zinc-600 text-white px-5 py-3 rounded-lg font-medium transition-colors disabled:opacity-50"
                              data-testid="validate-coupon-btn"
                            >
                              {isValidatingCoupon ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Apply'}
                            </button>
                          </div>
                          
                          {/* Coupon Validation Result */}
                          {couponValidation && (
                            couponValidation.valid ? (
                              <div className="bg-emerald-900/30 border border-emerald-700 rounded-lg p-4 mt-4">
                                <div className="flex items-center justify-between mb-2">
                                  <span className="text-emerald-400 font-medium flex items-center gap-2">
                                    <Check className="w-5 h-5" />
                                    Coupon Applied: {couponValidation.code}
                                  </span>
                                  <span className="text-emerald-400 font-bold">{couponValidation.discount_percent}% OFF</span>
                                </div>
                                <div className="flex items-center gap-3 text-sm">
                                  <span className="text-zinc-400 line-through">₹{couponValidation.original_price}</span>
                                  <span className="text-white font-bold text-lg">
                                    {couponValidation.final_price === 0 ? 'FREE' : `₹${couponValidation.final_price}`}
                                  </span>
                                  <span className="text-zinc-500">for 6 months</span>
                                </div>
                                
                                <button
                                  type="button"
                                  onClick={applyCoupon}
                                  disabled={isApplyingCoupon}
                                  className="w-full mt-4 bg-emerald-600 hover:bg-emerald-500 text-white py-3 rounded-lg font-semibold transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                                  data-testid="activate-profile-btn"
                                >
                                  {isApplyingCoupon ? (
                                    <>
                                      <Loader2 className="w-5 h-5 animate-spin" />
                                      {subscriptionStatus === 'active' ? 'Renewing...' : 'Activating...'}
                                    </>
                                  ) : (
                                    <>
                                      <Check className="w-5 h-5" />
                                      {subscriptionStatus === 'active' 
                                        ? (couponValidation.final_price === 0 ? 'Renew Profile (Free)' : `Renew Profile - Pay ₹${couponValidation.final_price}`)
                                        : (couponValidation.final_price === 0 ? 'Activate Profile (Free)' : `Activate Profile - Pay ₹${couponValidation.final_price}`)}
                                    </>
                                  )}
                                </button>
                              </div>
                            ) : (
                              <div className="bg-red-900/30 border border-red-700 rounded-lg p-4 mt-4">
                                <p className="text-red-400 flex items-center gap-2">
                                  <X className="w-5 h-5" />
                                  {couponValidation.error || 'Invalid coupon code'}
                                </p>
                              </div>
                            )
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
              
              {/* Email Signature Download - Only for active profiles */}
              {subscriptionStatus === 'active' && (
                <div className="border border-zinc-700 rounded-lg p-4 bg-zinc-800/50">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-white font-medium flex items-center gap-2">
                        <Download className="w-4 h-4 text-[#C41E3A]" />
                        Email Signature Strip
                      </h4>
                      <p className="text-zinc-500 text-sm mt-1">
                        Download a branded signature image for your email
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={openSignatureModal}
                      className="bg-[#C41E3A] hover:bg-[#A01830] text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
                      data-testid="download-signature-btn"
                    >
                      <Download className="w-4 h-4" />
                      Download
                    </button>
                  </div>
                </div>
              )}
              
              {/* Event Invitations - Pro or Event Invitations add-on */}
              {features && !canUseInvitations && (
                <FeatureLock title="Event Invitations" testId="invitations" />
              )}
              {canUseInvitations && (
                <div className="border border-zinc-700 rounded-lg p-4 bg-zinc-800/50">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h4 className="text-white font-medium flex items-center gap-2">
                        <CalendarDays className="w-4 h-4 text-[#C41E3A]" />
                        Event Invitations
                      </h4>
                      <p className="text-zinc-500 text-sm mt-1">
                        Create shareable event invitations linked to your profile
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => { setSelectedTemplate('blank'); setShowInvitationModal(true); }}
                      className="bg-[#C41E3A] hover:bg-[#A01830] text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
                      data-testid="create-invitation-btn"
                    >
                      <CalendarDays className="w-4 h-4" />
                      Create Invitation
                    </button>
                  </div>
                  
                  {/* List of existing invitations */}
                  {invitations.length > 0 && (
                    <div className="space-y-2 mt-4 pt-4 border-t border-zinc-700">
                      {invitations.map((inv) => (
                        <div 
                          key={inv.id} 
                          className={`flex items-center justify-between p-3 rounded-lg ${inv.is_expired ? 'bg-zinc-800/30 opacity-60' : 'bg-zinc-800'}`}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-white font-medium truncate">{inv.event_title}</span>
                              {inv.is_expired && (
                                <span className="text-xs bg-amber-900/50 text-amber-400 px-2 py-0.5 rounded">Expired</span>
                              )}
                            </div>
                            <p className="text-zinc-500 text-xs mt-0.5">
                              {inv.event_type} • {new Date(inv.event_date).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 ml-2">
                            <button
                              onClick={() => copyInvitationLink(inv.share_url)}
                              className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-700 rounded-lg transition-colors"
                              title="Copy link"
                              data-testid={`invitation-copy-${inv.id}`}
                            >
                              <Copy className="w-4 h-4" />
                            </button>
                            <a
                              href={inv.share_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-700 rounded-lg transition-colors"
                              title="Preview"
                              data-testid={`invitation-preview-${inv.id}`}
                            >
                              <ExternalLink className="w-4 h-4" />
                            </a>
                            <button
                              onClick={() => deleteInvitation(inv.id)}
                              className="p-2 text-zinc-400 hover:text-red-400 hover:bg-zinc-700 rounded-lg transition-colors"
                              title="Delete"
                              data-testid={`invitation-delete-${inv.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              
              {/* Already Active Notice - only show if not expiring soon (no renew needed) */}
              {subscriptionStatus === 'active' && (daysUntilExpiry === null || daysUntilExpiry > 30) && (
                <div className="bg-emerald-900/20 border border-emerald-700 rounded-lg p-4">
                  <div className="flex items-center gap-2">
                    <Check className="w-5 h-5 text-emerald-400" />
                    <span className="text-emerald-400 font-medium">Profile is Live</span>
                  </div>
                  <p className="text-zinc-400 text-sm mt-1">
                    Your profile is active and visible to the public
                    {activeUntil && ` until ${activeUntil.toLocaleDateString()}`}.
                  </p>
                </div>
              )}
            </div>
          )}
          
          {/* Navigation Buttons */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-zinc-700">
            {currentStep > 0 ? (
              <button
                type="button"
                onClick={prevStep}
                className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>
            ) : (
              <div />
            )}
            
            {currentStep < steps.length - 1 ? (
              <button
                type="button"
                onClick={nextStep}
                className="flex items-center gap-2 bg-[#C41E3A] hover:bg-[#A01830] text-white px-6 py-3 rounded-lg font-semibold transition-colors"
              >
                Next
                <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                type="button"
                onClick={saveProfile}
                disabled={isSubmitting}
                className="flex items-center gap-2 bg-[#C41E3A] hover:bg-[#A01830] text-white px-6 py-3 rounded-lg font-semibold transition-colors disabled:opacity-50"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Save Changes
                  </>
                )}
              </button>
            )}
          </div>
        </div>
        
        {/* Footer note */}
        <p className="text-center text-zinc-500 text-xs mt-6">
          Changes will be visible on your public profile immediately after saving.
        </p>
      </div>
      
      {/* Testimonial Request Modal */}
      {showTestimonialModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 border border-zinc-700 rounded-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Send className="w-5 h-5 text-[#C41E3A]" />
                Request Testimonial
              </h3>
              <button
                onClick={() => setShowTestimonialModal(false)}
                className="text-zinc-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <p className="text-zinc-400 text-sm mb-4">
              We&apos;ll send an email to your contact with a link to record a video testimonial.
            </p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-zinc-300 text-sm font-medium mb-1">Contact Name *</label>
                <input
                  type="text"
                  value={testimonialRequest.contact_name}
                  onChange={(e) => setTestimonialRequest(prev => ({ ...prev, contact_name: e.target.value }))}
                  placeholder="John Smith"
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2.5 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50"
                  data-testid="testimonial-contact-name"
                />
              </div>
              
              <div>
                <label className="block text-zinc-300 text-sm font-medium mb-1">Contact Email *</label>
                <input
                  type="email"
                  value={testimonialRequest.contact_email}
                  onChange={(e) => setTestimonialRequest(prev => ({ ...prev, contact_email: e.target.value }))}
                  placeholder="john@company.com"
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2.5 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50"
                  data-testid="testimonial-contact-email"
                />
              </div>
              
              <div>
                <label className="block text-zinc-300 text-sm font-medium mb-1">Personal Message (Optional)</label>
                <textarea
                  value={testimonialRequest.message}
                  onChange={(e) => setTestimonialRequest(prev => ({ ...prev, message: e.target.value }))}
                  placeholder="Hi John, we'd love to hear about your experience working with us..."
                  rows={3}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2.5 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50 resize-none"
                  data-testid="testimonial-message"
                />
              </div>
            </div>
            
            {error && (
              <p className="text-red-400 text-sm mt-3">{error}</p>
            )}
            
            <div className="flex gap-3 mt-6">
              <button
                type="button"
                onClick={() => setShowTestimonialModal(false)}
                className="flex-1 bg-zinc-700 hover:bg-zinc-600 text-white py-2.5 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={requestTestimonial}
                disabled={isRequestingTestimonial || !testimonialRequest.contact_name || !testimonialRequest.contact_email}
                className="flex-1 bg-[#C41E3A] hover:bg-[#A01830] text-white py-2.5 rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="send-testimonial-request-btn"
              >
                {isRequestingTestimonial ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Send Request
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Email Signature Customization Modal */}
      {showSignatureModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-zinc-900 border border-zinc-700 rounded-2xl p-6 max-w-md w-full relative">
            <button
              onClick={() => setShowSignatureModal(false)}
              className="absolute top-4 right-4 text-zinc-400 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>
            
            <h3 className="text-xl font-bold text-white mb-2">Customize Signature</h3>
            <p className="text-zinc-400 text-sm mb-4">
              Personalize your email signature with your details. Company branding stays the same.
            </p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-zinc-400 text-sm mb-1">Your Name *</label>
                <input
                  type="text"
                  value={signatureData.name}
                  onChange={(e) => setSignatureData({...signatureData, name: e.target.value})}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C41E3A]"
                  placeholder="John Smith"
                  data-testid="signature-name-input"
                />
              </div>
              
              <div>
                <label className="block text-zinc-400 text-sm mb-1">Designation *</label>
                <input
                  type="text"
                  value={signatureData.designation}
                  onChange={(e) => setSignatureData({...signatureData, designation: e.target.value})}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C41E3A]"
                  placeholder="CEO, Head of Sales, etc."
                  data-testid="signature-designation-input"
                />
              </div>
              
              <div>
                <label className="block text-zinc-400 text-sm mb-1">Email *</label>
                <input
                  type="email"
                  value={signatureData.email}
                  onChange={(e) => setSignatureData({...signatureData, email: e.target.value})}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C41E3A]"
                  placeholder="your.email@company.com"
                  data-testid="signature-email-input"
                />
              </div>
              
              <div>
                <label className="block text-zinc-400 text-sm mb-1">Phone (Optional)</label>
                <input
                  type="tel"
                  value={signatureData.phone}
                  onChange={(e) => setSignatureData({...signatureData, phone: e.target.value})}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C41E3A]"
                  placeholder="+91 98765 43210"
                  data-testid="signature-phone-input"
                />
              </div>
            </div>
            
            <div className="mt-6 flex gap-3">
              <button
                onClick={() => setShowSignatureModal(false)}
                className="flex-1 bg-zinc-800 hover:bg-zinc-700 text-white py-2.5 rounded-lg text-sm font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={downloadEmailSignature}
                disabled={!signatureData.name || !signatureData.email || isGeneratingSignature}
                className="flex-1 bg-[#C41E3A] hover:bg-[#A01830] disabled:bg-zinc-700 disabled:cursor-not-allowed text-white py-2.5 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                data-testid="generate-signature-btn"
              >
                {isGeneratingSignature ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4" />
                    Generate & Download
                  </>
                )}
              </button>
            </div>
            
            <p className="text-zinc-500 text-xs text-center mt-4">
              Company logo and branding from {formData.company_name || 'your profile'}
            </p>
          </div>
        </div>
      )}
      
      {/* Event Invitation Creation Modal */}
      {showInvitationModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-zinc-900 border border-zinc-700 rounded-2xl p-6 max-w-lg w-full relative my-8">
            <button
              onClick={() => setShowInvitationModal(false)}
              className="absolute top-4 right-4 text-zinc-400 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>
            
            <h3 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
              <CalendarDays className="w-5 h-5 text-[#C41E3A]" />
              Create Event Invitation
            </h3>
            <p className="text-zinc-400 text-sm mb-4">
              Start from a template or create from scratch
            </p>

            {/* Template Selector */}
            <div className="mb-4">
              <label className="block text-zinc-400 text-xs mb-2 font-medium">Quick Start — Choose a Template</label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {[
                  { key: 'blank', label: 'Blank', icon: '📝', desc: 'Start from scratch' },
                  { key: 'roundtable', label: 'Roundtable', icon: '🤝', desc: 'High-level roundtable discussion',
                    data: { event_type: 'roundtable', event_title: '', description: 'A high-level roundtable bringing together industry leaders and policymakers to discuss collaboration opportunities and share best practices.', objectives: '1. Foster cross-border partnerships\n2. Explore investment and market-entry strategies\n3. Share best practices and insights' }},
                  { key: 'product_launch', label: 'Product Launch', icon: '🚀', desc: 'Launch event for a new product',
                    data: { event_type: 'conference', event_title: '', description: 'Join us for the exclusive launch of our latest product. Experience live demonstrations, meet our leadership team, and be among the first to discover what is next.', objectives: '1. Showcase the new product to key stakeholders\n2. Generate media and partner interest\n3. Collect early feedback from industry experts' }},
                  { key: 'trade_mission', label: 'Trade Mission', icon: '🌍', desc: 'International trade delegation',
                    data: { event_type: 'networking', event_title: '', description: 'An exclusive trade mission connecting businesses with international partners, government representatives, and investment agencies to explore new markets.', objectives: '1. Facilitate B2B matchmaking sessions\n2. Present market-entry opportunities\n3. Build relationships with local authorities and business chambers' }},
                  { key: 'workshop', label: 'Workshop', icon: '🔧', desc: 'Hands-on learning session',
                    data: { event_type: 'workshop', event_title: '', description: 'An interactive workshop designed to deliver practical skills and insights. Participants will engage in hands-on activities, group discussions, and real-world case studies.', objectives: '1. Deliver practical, actionable takeaways\n2. Enable peer learning and networking\n3. Provide frameworks applicable to participants\' work' }},
                  { key: 'networking', label: 'Networking', icon: '🥂', desc: 'Professional networking event',
                    data: { event_type: 'networking', event_title: '', description: 'An evening of professional networking, bringing together business leaders, entrepreneurs, and industry professionals in an informal setting.', objectives: '1. Connect professionals across industries\n2. Foster potential business partnerships\n3. Build a collaborative community' }},
                ].map(t => (
                  <button
                    key={t.key}
                    onClick={() => {
                      setSelectedTemplate(t.key);
                      if (t.data) {
                        setInvitationForm(prev => ({ ...prev, ...t.data }));
                      } else {
                        setInvitationForm({ event_title: '', event_type: 'seminar', event_date: '', event_time: '', venue: '', description: '', objectives: '', banner_image_url: '' });
                      }
                    }}
                    className={`flex items-start gap-2 p-2.5 rounded-lg border transition-all text-left group ${
                      selectedTemplate === t.key
                        ? 'border-[#C41E3A] bg-[#C41E3A]/10 ring-1 ring-[#C41E3A]/30'
                        : 'border-zinc-700 bg-zinc-800/50 hover:border-[#C41E3A]/50 hover:bg-[#C41E3A]/5'
                    }`}
                    data-testid={`template-${t.key}`}
                  >
                    <span className="text-lg shrink-0">{t.icon}</span>
                    <div className="min-w-0">
                      <p className={`text-xs font-semibold transition-colors ${selectedTemplate === t.key ? 'text-[#C41E3A]' : 'text-white group-hover:text-[#C41E3A]'}`}>{t.label}</p>
                      <p className="text-zinc-500 text-[10px] line-clamp-1">{t.desc}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
            
            <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
              <div>
                <label className="block text-zinc-400 text-sm mb-1">Event Title *</label>
                <input
                  type="text"
                  value={invitationForm.event_title}
                  onChange={(e) => setInvitationForm({...invitationForm, event_title: e.target.value})}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C41E3A]"
                  placeholder="Industry Roundtable 2026"
                  data-testid="invitation-title-input"
                />
              </div>
              
              <div>
                <label className="block text-zinc-400 text-sm mb-1">Event Type *</label>
                <select
                  value={invitationForm.event_type}
                  onChange={(e) => setInvitationForm({...invitationForm, event_type: e.target.value})}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C41E3A]"
                  data-testid="invitation-type-select"
                >
                  <option value="seminar">Seminar</option>
                  <option value="workshop">Workshop</option>
                  <option value="panel_discussion">Panel Discussion</option>
                  <option value="networking">Networking Event</option>
                  <option value="conference">Conference</option>
                  <option value="roundtable">Roundtable</option>
                  <option value="webinar">Webinar</option>
                  <option value="other">Other</option>
                </select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-zinc-400 text-sm mb-1">Date *</label>
                  <input
                    type="date"
                    value={invitationForm.event_date}
                    onChange={(e) => setInvitationForm({...invitationForm, event_date: e.target.value})}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C41E3A]"
                    data-testid="invitation-date-input"
                  />
                </div>
                <div>
                  <label className="block text-zinc-400 text-sm mb-1">Time</label>
                  <input
                    type="text"
                    value={invitationForm.event_time}
                    onChange={(e) => setInvitationForm({...invitationForm, event_time: e.target.value})}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C41E3A]"
                    placeholder="10:00 AM - 1:00 PM"
                    data-testid="invitation-time-input"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-zinc-400 text-sm mb-1">Venue / Location *</label>
                <input
                  type="text"
                  value={invitationForm.venue}
                  onChange={(e) => setInvitationForm({...invitationForm, venue: e.target.value})}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C41E3A]"
                  placeholder="The Grand Hotel, Mumbai or 'Virtual'"
                  data-testid="invitation-venue-input"
                />
              </div>
              
              <div>
                <label className="block text-zinc-400 text-sm mb-1">Description *</label>
                <textarea
                  value={invitationForm.description}
                  onChange={(e) => setInvitationForm({...invitationForm, description: e.target.value})}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C41E3A] min-h-[100px]"
                  placeholder="Describe your event..."
                  data-testid="invitation-description-input"
                />
              </div>
              
              <div>
                <label className="block text-zinc-400 text-sm mb-1">Objectives (Optional)</label>
                <textarea
                  value={invitationForm.objectives}
                  onChange={(e) => setInvitationForm({...invitationForm, objectives: e.target.value})}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C41E3A] min-h-[80px]"
                  placeholder="Key objectives of this event..."
                  data-testid="invitation-objectives-input"
                />
              </div>
              
              <div>
                <label className="block text-zinc-400 text-sm mb-1">Banner Image URL (Optional)</label>
                <input
                  type="url"
                  value={invitationForm.banner_image_url}
                  onChange={(e) => setInvitationForm({...invitationForm, banner_image_url: e.target.value})}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C41E3A]"
                  placeholder="https://..."
                  data-testid="invitation-banner-input"
                />
              </div>
            </div>
            
            <div className="mt-6 flex gap-3">
              <button
                onClick={() => setShowInvitationModal(false)}
                className="flex-1 bg-zinc-800 hover:bg-zinc-700 text-white py-2.5 rounded-lg text-sm font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={createInvitation}
                disabled={!invitationForm.event_title || !invitationForm.event_date || !invitationForm.venue || !invitationForm.description || isCreatingInvitation}
                className="flex-1 bg-[#C41E3A] hover:bg-[#A01830] disabled:bg-zinc-700 disabled:cursor-not-allowed text-white py-2.5 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                data-testid="create-invitation-submit-btn"
              >
                {isCreatingInvitation ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <CalendarDays className="w-4 h-4" />
                    Create Invitation
                  </>
                )}
              </button>
            </div>
            
            <p className="text-zinc-500 text-xs text-center mt-4">
              Your company logo and branding will be automatically included
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
